import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import memoize from 'memoize-one';
import isEqual from 'lodash/isEqual';
import { styled } from '@compiled/react';
import { graphql } from 'react-relay';

import { EmojiResource } from '@atlaskit/emoji/resource';
import { insm } from '@atlaskit/insm';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { EditorActions } from '@atlaskit/editor-core';
import type { InlineCommentPluginState } from '@atlaskit/editor-plugin-annotation';
import type { ExtensionManifest } from '@atlaskit/editor-common/extensions';
import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';
import UFOLabel from '@atlaskit/react-ufo/label';

import { EditorForgeExtensionConfigHelperRenderer } from '@atlassian/xen-editor-provider';
import type { SmartPrompt } from '@atlassian/conversation-assistant-blank-object-experience/types';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';

import { isMac } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	useCommentsPanelActions,
	ViewValues,
	SidebarTabValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { useSnippetExtension } from '@confluence/snippets/entry-points/useSnippetExtension.ts';
import { useInsertSnippetOnLoad } from '@confluence/snippets/entry-points/useInsertSnippetOnLoad.ts';
import { EditorCommentShortcutListener } from '@confluence/comments-shortcuts/entry-points/EditorCommentShortcutListener.ts';
import { ObjectCommentProvider } from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { InlineCommentsProvider } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { ReactionsProvider } from '@confluence/comment-context/entry-points/ReactionsContext.ts';
import { EndOfPageRecommendationLoader } from '@confluence/page-recommendations/entry-points/EndOfPageRecommendationLoader.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { fg } from '@confluence/feature-gating';
import { PageCommentsPubSubHandler } from '@confluence/comments-pubsub/entry-points/pageComments.ts';
import { useIsMentionOnRestrictedPageEligible } from '@confluence/experiment-mention-on-restricted-page';
import {
	expValEquals,
	expVal,
	isExperimentEnabled,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import type { LoadingPriority } from '@confluence/loadable';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue.ts';
import { MovePageSuccessFlag } from '@confluence/page-tree/entry-points/MovePageSuccessFlag.tsx';
import { withPageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container/entry-points/withPageTreeStateUpdater.ts';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { CommentsDataBackgroundLoader } from '@confluence/comments-data-manager/entry-points/CommentsDataBackgroundLoader.tsx';
import { useAllCommentsData } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';

// The DO_NOT_USE here is an artifact of an old implementation. We can, in fact, use this

import { SmartButtonConfigPanel as EditorContextSmartButtonConfigPanel } from '@confluence/automation-extensions/entry-points/SmartButtonConfigPanel.ts';
import { useSmartButtonPremiumGate } from '@confluence/automation-extensions/entry-points/useSmartButtonPremiumGate.ts';
import { useSmartButtonConfigPanel } from '@confluence/automation-extensions/entry-points/useSmartButtonConfigPanel.ts';
import { QuickReload } from '@confluence/quick-reload/entry-points/QuickReload.ts';
import { PageMode } from '@confluence/quick-reload/entry-points/pubSubConstants.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { waitForGlobal } from '@confluence/wrm/entry-points/waitForGlobal.ts';
import {
	VIEW_ALL_MACRO_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
	OBJECT_HEADER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { useRetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent.ts';
import { getCachedPageTitle } from '@confluence/editor-features/entry-points/utils/getCachedPageTitle.ts';
import { EditorAnalyticsListener } from '@confluence/fabric-analytics-listeners/entry-points/EditorAnalyticsListener.ts';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithDialogsProps } from '@confluence/dialogs/entry-points/withDialogs.ts';
import { withDialogs } from '@confluence/dialogs/entry-points/withDialogs.ts';
import {
	TriggerSaveIndicatorContext,
	SaveIndicatorContextProvider,
} from '@confluence/save-indicator/entry-points/SaveIndicatorContext.ts';
// Metrics and Instrumentation related
import { PageLoadMark } from '@confluence/browser-metrics/entry-points/PageLoadMark.ts';
import { StickyDraftCommentContainer } from '@confluence/object-comments/entry-points/StickyDraftCommentContainer.ts';
import { ObjectCommentWrapper } from '@confluence/object-comments/entry-points/ObjectCommentWrapper.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
// Editor common functionalities and providers
import { useGetChannelVariables } from '@confluence/editor-features/entry-points/useGetChannelVariables.ts';
import { createFallbackMediaNameFetcher } from '@confluence/editor-features/entry-points/fallbackMediaNameFetcher.ts';
import { type WithDocChangeContextProps } from '@confluence/editor-features/entry-points/WithDocChangeContextProps.ts';
import { type ProseMirrorRenderedDataType } from '@confluence/editor-features/entry-points/ProseMirrorRenderedDataType.ts';
import { useActivityProvider } from '@confluence/editor-features/entry-points/useActivityProvider.ts';
import { useAutoformattingProvider } from '@confluence/editor-features/entry-points/useAutoformattingProvider.ts';
import { useContextIdentifierProvider } from '@confluence/editor-features/entry-points/useContextIdentifierProvider.ts';
import { useDraftSyncAndPublish } from '@confluence/editor-features/entry-points/useDraftSyncAndPublish.ts';
import { useEditorAnalyticsEvent } from '@confluence/editor-features/entry-points/useEditorAnalyticsEvent.ts';
import { useEditorFeatureFlags } from '@confluence/editor-features/entry-points/useEditorFeatureFlags.ts';
import { useEditorTitleFocus } from '@confluence/editor-features/entry-points/useEditorTitleFocus.ts';
import { getFindReplaceOptions } from '@confluence/editor-features/entry-points/getFindReplaceOptions.ts';
import { useHandleCollabError } from '@confluence/editor-features/entry-points/useHandleCollabError.ts';
import { useInjectDraftContent } from '@confluence/editor-features/entry-points/useInjectDraftContent.ts';
import { useMediaOptions } from '@confluence/editor-features/entry-points/useMediaOptions.ts';
import { useMemoizedSyncedBlockProviderForEditor } from '@confluence/editor-features/entry-points/useSyncedBlockProvider.ts';
import { useMentionProvider } from '@confluence/editor-features/entry-points/useMentionProvider.ts';
import { useSearchProvider } from '@confluence/editor-features/entry-points/useSearchProvider.ts';
import { useSpinner, Spinner } from '@confluence/editor-features/entry-points/useSpinner.ts';
import { useEditorPluginAIOptions } from '@confluence/editor-features/entry-points/useEditorPluginAIOptions.ts';
import { useEditorPluginAIFeedbackHandlers } from '@confluence/editor-features/entry-points/useEditorPluginAIFeedbackHandlers.ts';
import { useEditorPluginAICollabConnector } from '@confluence/editor-features/entry-points/useEditorPluginAICollabConnector.ts';
import { useDocumentActive } from '@confluence/editor-features/entry-points/useDocumentActive.ts';
import { useEditorDraftSyncAction } from '@confluence/editor-features/entry-points/useEditorDraftSyncAction.ts';
import { useRefetchNativeCollab } from '@confluence/editor-features/entry-points/useRefetchNativeCollab.ts';
import { EditorWrapper } from '@confluence/editor-features/entry-points/EditorWrapper.ts';
import { EditorDisplayWrapper } from '@confluence/editor-features/entry-points/EditorDisplayWrapper.ts';
import { EditorLinkingFeatures } from '@confluence/editor-features/entry-points/EditorLinkingFeatures.ts';
import { PageLoadWrapper } from '@confluence/editor-features/entry-points/PageLoadWrapper.ts';
import { EditorPluginAIWithFeedback } from '@confluence/editor-features/entry-points/EditorPluginAIWithFeedback.ts';
import { useEditorAIOptIn } from '@confluence/editor-features/entry-points/useEditorAIOptIn.ts';
import { withSmartCardProviderContext } from '@confluence/editor-features/entry-points/withSmartCardProviderContext.ts';
import { withDocChangeContext } from '@confluence/editor-features/entry-points/withDocChangeContext.ts';
import {
	// HOCs
	withEditorActions,
} from '@confluence/editor-features/entry-points/withEditorActions.ts';
import { EditorContextContentCreationPanelWrapper } from '@confluence/editor-templates/entry-points/EditorContextContentCreationPanelWrapper.ts';
import { EditorContextPanelContextProvider } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContextProvider.ts';
import { EditorContextPanelView } from '@confluence/editor-context-panel-view';
import { useEditorAnnotationProviderOptions } from '@confluence/annotation-provider/entry-points/useEditorAnnotationProviderOptions.ts';
import { EditorAnnotationProviderComponents } from '@confluence/annotation-provider-inline-comments/entry-points/editor.ts';
import { useEditorAnnotationsActions } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations.ts';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import { useUpdateAnnotations } from '@confluence/comments-data/entry-points/useUpdateAnnotations.ts';
import { useEditorSSRExtensionHandlers } from '@confluence/fabric-extension-handlers/entry-points/editorSSRExtensionHandlers.ts';
import { useExtensionOptions } from '@confluence/fabric-extension-handlers/entry-points/useExtensionOptions.ts';
import { getTaskDecisionProvider } from '@confluence/fabric-providers/entry-points/TaskDecisionProvider.ts';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { useProfilecardProvider } from '@confluence/fabric-providers/entry-points/useProfileCardProvider.ts';
import { MentionsNotificationsWrapper } from '@confluence/mentions-notifications/entry-points/MentionsNotificationsWrapper.ts';
// Editor UI components
import { DocumentEditPageTitle } from '@confluence/document-title/entry-points/DocumentEditPageTitle.ts';
import { EditorTypes, PreviewPageComponent } from '@confluence/editor-preview';
import {
	ContentAppearanceContextProvider,
	ContentAppearanceContextConsumer,
} from '@confluence/content-appearance/entry-points/ContentAppearanceContext.ts';
import { type FullPageAppearances } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import { getContentAppearanceForAtlaskit } from '@confluence/content-appearance/entry-points/atlaskitTransformations.ts';
import { PageContentRenderer } from '@confluence/content-renderer/entry-points/PageContentRenderer.ts';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import {
	useEditorRevertDialogContext,
	withRevertContext,
} from '@confluence/editor-revert-dialog/entry-points/EditorRevertDialogContext.ts';
import { EditorRevertDialog } from '@confluence/editor-revert-dialog/entry-points/EditorRevertDialog.ts';
import { OnboardingEditorModalPrompt } from '@confluence/onboarding-editor';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import type {
	EditorWrapperProps,
	ContentTypeType,
} from '@confluence/editor-features/entry-points/types.ts';
import { ApprovalInProgressEditorBlocker } from '@confluence/approvals/entry-points/ApprovalInProgressEditorBlocker.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { EditorConnectionBannerWrapper } from '@confluence/browser-status-indicator/entry-points/EditorConnectionBannerWrapper.ts';
import { EDITOR } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import {
	useRouteActions,
	useMatchRoute,
	createSingleQueryParamHook,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { withEditorModalContext } from '@confluence/editor-modal';
import { scrollToLastViewedPosition } from '@confluence/editor-scroll-position/entry-points/scrollToLastViewedPosition.ts';
import { measureTimeToEdit } from '@confluence/editor-scroll-position/entry-points/measureTimeToEdit.ts';
import {
	useNativeCollabState,
	useNativeCollabConnected,
} from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { isDefaultLiveDocTitle } from '@confluence/live-pages-utils/entry-points/isDefaultLiveDocTitle.ts';
import { CollabServiceProperty } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import type { NativeCollabInjectedProps } from '@confluence/native-collab/entry-points/commonCollabTypes.ts';
import { isUnrecoverableError } from '@confluence/native-collab/entry-points/useError.ts';
import {
	EditorContextConnectionsPanel,
	useConnectionsPanel,
} from '@confluence/app-referentiality/entry-points/EditorContextConnectionsPanel.ts';
import { getReferentialityContext } from '@confluence/app-referentiality';
import { getMarketplaceAppsContainer } from '@confluence/app-suggestions/entry-points/index.ts';
import { useEditSource } from '@confluence/edit-common/entry-points/useEditSource.ts';
import { ConditionalPublish } from '@confluence/conditional-publish-dialog';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { ConvertToLivePageFlag } from '@confluence/live-pages-features/entry-points/ConvertToLivePageFlag.tsx';
import { useContentTypesTitleUpdate } from '@confluence/content-types-header/entry-points/useContentTypesTitleUpdate.ts';
import { useUpdateLastEditedTimestampVersionCache } from '@confluence/content-types-utils/entry-points/useUpdateLastEditedTimestampVersionCache.ts';
import { MoveToPersonalSpaceFlagTrigger } from '@confluence/content-types-utils/entry-points/MoveToPersonalSpaceFlagTrigger.ts';
import { EditorObjectHeaderContextProvider } from '@confluence/content-types-header/entry-points/EditorObjectHeaderContextProvider.ts';
import { useOptimisticTitleChange } from '@confluence/page-tree-refresh-state-container/entry-points/useOptimisticTitleChange.ts';
import {
	EditorContextCustomSitesConfigPanelEntryPoint,
	useCustomExtensionConfigPanel,
} from '@confluence/custom-sites-extensions/entry-points/editorConfigPanel.ts';
import {
	AutoConvertEvent,
	AutoConvertActionSubject,
	sendEditorConversionAnalyticsPageEvent,
} from '@confluence/editor-conversion/entry-points/analytics.ts';
import { useAutoConvertState } from '@confluence/editor-conversion/entry-points/useAutoConvertState.ts';
import {
	PrimaryToolbarContextProvider,
	usePrimaryToolbarContext,
} from '@confluence/editor-primary-toolbar-components/entry-points/PrimaryToolbarContext.ts';
import { EditorPrimaryToolbarComponents } from '@confluence/editor-primary-toolbar-components/entry-points/EditorPrimaryToolbarComponents.ts';
import { ContentComponentsContextProvider } from '@confluence/title-area-content-components/entry-points/ContentComponentsContext.tsx';
import { EditorActionBar } from '@confluence/editor-action-bar/entry-points/EditorActionBar.ts';
import { SmartCreateFeedbackBannerLoader } from '@confluence/editor-action-bar/entry-points/SmartCreateFeedbackBannerLoader.ts';
import { ContentTypesHeader } from '@confluence/content-types-header/entry-points/ContentTypesHeader.tsx';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { BlankPageQuickActions } from '@confluence/blank-page-quick-actions/entry-points/BlankPageQuickActions.ts';
import { ImportedPagePolishBanner } from '@confluence/experiment-cfind-5926-upload-in-onboarding/entry-points/ImportedPagePolishBanner.ts';
import { BlankPageQuickActionsTemplatePreview } from '@confluence/blank-page-quick-actions/entry-points/BlankPageQuickActionsTemplatePreview.ts';
import { BlankPageQuickActionsProvider } from '@confluence/blank-page-quick-actions/entry-points/BlankPageQuickActionsContext.ts';
import { BlankObjectExperienceButtons } from '@confluence/blank-page-quick-actions/entry-points/BlankObjectExperienceButtons.ts';
import { useShouldShowBlankPageQuickActions } from '@confluence/blank-page-quick-actions/entry-points/useShouldShowBlankPageQuickActions.ts';
import { useContextualInviteNudge } from '@confluence/onboarding-helpers/entry-points/hooks/useContextualInviteNudge.ts';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries.ts';
import { InlineCommentMode } from '@confluence/inline-comments-utils/entry-points/enum.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import { isCommentNotFoundError } from '@confluence/comments-util/entry-points/errorUtils.ts';
import { getSortedMarkerRefs } from '@confluence/comments-util/entry-points/domUtils.ts';
import { useHeadingLinksPathBuilder } from '@confluence/editor-heading-links/entry-points/useHeadingLinksPathBuilder.ts';
import { HeadingLinksStyleWrapper } from '@confluence/editor-heading-links/entry-points/HeadingLinksStyleWrapper.ts';
import { GuestMentionsNotificationsWrapper } from '@confluence/external-collab-ui/entry-points/GuestMentionsNotificationsWrapper.ts';
import { HubEditorWrapper } from '@confluence/company-hub/entry-points/hub-layout.ts';
import { MultiMacroQueryContainer } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { useInviteFromLinkExperimentEditor } from '@confluence/invite-on-link-insert/entry-points/useInviteFromLinkExperimentEditor.ts';
import { useRequestToEditContent } from '@confluence/no-permission/entry-points/useRequestToEditContent.tsx';
import { useExtensionRendererViewOptions } from '@confluence/fabric-extension-lib/entry-points/useExtensionRendererViewOptions.ts';
import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import { PageFooterComments } from '@confluence/page-footer-comments';
import { PublishModalKeyboardShortcuts } from '@confluence/publish-modal/entry-points/PublishModalKeyboardShortcuts.ts';
import { PublishModalMountPoint } from '@confluence/publish-modal/entry-points/PublishModalMountPoint.ts';
import { usePrePublishTitleValidation } from '@confluence/publish-modal/entry-points/usePrePublishTitleValidation.ts';
import { usePublishModalMountActions } from '@confluence/publish-modal/entry-points/PublishModalMountState.ts';
import { useEmbeddedEditPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/edit-page.ts';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { usePageNativeEmbedClient } from '@confluence/embedded-confluence-allowed-features/entry-points/usePageNativeEmbedClient.ts';
import type { LegacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import { ClassificationModalContextProvider } from '@confluence/data-classification/entry-points/ClassificationModalContext.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { useExtensionNodeParams } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/useExtensionNodeParams.ts';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';
import { RovoBridgeCommandListener } from '@confluence/rovo-editor-bridge-listener/entry-points/RovoBridgeCommandListener.ts';
import { usePrintIntercept } from '@confluence/pdf-print-utils/entry-points/print.ts';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';
import { TaskScrollComponent } from '@confluence/task/entry-points/TaskScrollComponent.ts';
import { createSmartCardEventHandler } from '@confluence/linking-platform/entry-points/createSmartCardEventHandler.ts';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import { CreateWithRovoExperience } from '@confluence/blank-page-quick-actions/entry-points/CreateWithRovoExperience.ts';
import { CreateWithRovoExperienceDismissText } from '@confluence/blank-page-quick-actions/entry-points/CreateWithRovoExperienceDismissText.ts';
import { useMediaCommentExtensions } from '@confluence/editor-features/entry-points/useMediaCommentExtensions.ts';
import { useIntegrationLinkSpotlightForFullPageEditor } from '@confluence/integration-link-spotlight/entry-points/useIntegrationLinkSpotlightForFullPageEditor.ts';
import { useDetectPastedThirdPartyLinksForFullPageEditor } from '@confluence/integration-link-spotlight/entry-points/useDetectPastedThirdPartyLinksForFullPageEditor.ts';
import { IntegrationLinkSpotlightLoadable } from '@confluence/integration-link-spotlight/entry-points/IntegrationLinkSpotlightLoadable.ts';
import { useOpenSmartCreationModalFromQuickInsert } from '@confluence/experiment-create-with-rovo-quick-insert/entry-points/useOpenSmartCreationModalFromQuickInsert.ts';
import { LazyLargePageSplitCandidateDetector } from '@confluence/page-splitter/entry-points/LazyLargePageSplitCandidateDetector.tsx';
import { getHasAudioButton } from '@confluence/object-sidebar-components/entry-points/getHasAudioButton.ts';

import { useLiveDocTransitionCallback } from './hooks/useLiveDocTransitionCallback.ts';
import { FullPageComposableEditor } from './AkEditor/ComposableEditor.tsx';
import { OnboardingHelpContainer } from './OnboardingHelpContainer.tsx';
import { recordSSRMeasure } from './utils/recordSSRMeasure.ts';
import {
	ANALYTICS_SOURCE,
	EASY_MENTIONS_PLACEHOLDER_TEXT_ID,
	EASY_MENTIONS_PLACEHOLDER_DEFAULT_MESSAGE,
	AI_PLACEHOLDER_TEXT_ID,
	AI_PLACEHOLDER_DEFAULT_MESSAGE,
} from './fullPageEditorConstants.ts';
import { i18n } from './i18n.ts';
import { useMacroFallbackFlag } from './hooks/useMacroFallbackFlag.tsx';
import { useEditorFullPageExperiments } from './hooks/useEditorFullPageExperiments.ts';
import { EditorNavigationLockout, useHasUnsavedChanges } from './EditorNavigationLockout.tsx';
import { TitleAreaContentComponentsWrapper } from './TitleAreaContentComponentsWrapper.tsx';
import { useStartLivePageEditExperience } from './hooks/useStartLivePageEditExperience.ts';
import { useEditorContentDisabled } from './hooks/useEditorContentDisabled.ts';
import { useEditorActions } from './hooks/useEditorActions.ts';
import { FullPageEditorBannerWrapper } from './FullPageEditorBannerWrapper.tsx';
import { ObjectSidebarControlFullPageEditor } from './ObjectSidebarControlFullPageEditor.tsx';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { withRovoSystemSpaceNotFound } from './withRovoSystemSpaceNotFound.tsx';
import { RenderMarker } from './utils/RenderMarker.tsx';
import { ConditionalSideEffectsComponent } from './hooks/useConditionalSideEffects.tsx';

const messages = defineMessages({
	suggestEditsSkillPrompt: {
		id: 'full-page-editor.rovo.suggest-edits-skill.prompt',
		defaultMessage: 'Review my content with {skillCommand}',
		description:
			'Prompt submitted to Rovo chat when the user clicks the Suggest edits button in the editor toolbar. {skillCommand} is the slash command name (e.g. /suggest-edits) and must not be translated. Do not add punctuation after the skillCommand as it might affect how the skill gets parsed.',
	},
});

const ConditionalTwoStageHydration: React.FC<React.PropsWithChildren> = ({ children }) => {
	// cc_editor_two_stage_hydration_removal - When cleaning up this experiment, also remove the related TwoStageHydration component
	if (
		expValEquals(
			'cc_editor_two_stage_hydration_removal',
			'fullPageEditorComponentContentComponentsAfter',
			true,
		)
	) {
		return <>{children}</>;
	}

	return (
		<TwoStageHydration name="full-page-editor-contentcomponents-after">
			{children}
		</TwoStageHydration>
	);
};

const SSR_TRACE_SEGMENT_NAME = 'ssr-app/render/fullPageEditor';
const recordSSRMeasureWithPrefix: typeof recordSSRMeasure = ({ segmentName, ...rest }) =>
	recordSSRMeasure({
		segmentName: `${SSR_TRACE_SEGMENT_NAME}/${segmentName}`,
		...rest,
	});

export type FullPageEditorComponentProps = NativeCollabInjectedProps & {
	contentId: string;
	editorActions: EditorActions;
	spaceKey: string;
	spaceId: string | undefined;
	extensionContextProvider?: {
		ClassicEditorContextProvider: any;
	};
	/**
	 * defaults to true
	 * used by live pages as live pages render editor on view only mode
	 */
	hasEditPermission: boolean;
	/* supply customPerfData object to add custom properties to all `performance measured` events that use EDITOR_METRIC page segment */
	customPerfData?: Record<string, string | boolean | object>;
	onDraftSyncSuccess?: () => void; // Passed to useEditorDraftSyncAction as a callback when sync is successful.
	loadingPriority?: LoadingPriority | null;
	isFastLoadedLiveDoc?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
	hasPageComments: boolean;
	hasInlineComments: boolean;
	isTemplateBrowserEnabled: boolean;
	pageTreeStateUpdater?: PageTreeStateUpdater;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullPageEditorWrapper = styled.div({
	isolation: 'isolate',
});

// IMPORTANT: If you change the code below, please also update the
// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/pastePluginOptions.ts
// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/cardPluginOptions.ts
const CARD_OPTIONS = { allowDatasource: true };

const useLinkCreationQueryParam = createSingleQueryParamHook('linkCreation');
const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useFocusedMentionIdQueryParam = createSingleQueryParamHook('focusedMentionId');
const useReplyToCommentIdQueryParam = createSingleQueryParamHook('replyToComment');
const useVersionOverrideQueryParam = createSingleQueryParamHook('versionOverride');
const usePageVersionQueryParam = createSingleQueryParamHook('pageVersion');

// Content components are displayed below the editor tool bar
const _getContentComponents = memoize(
	({
		contentId,
		cloudId,
		debounceTitleChangeUpdates,
		focusEditor,
		onFocusChanged,
		userOffline,
		linkCreation,
		editorPluginAIFeedbackHandlers,
		isAdminHubAIEnabled,
		isAIEnabledForContent,
		isAIEarlyAccessEnabled,
		hideSpinner,
		templateSuggestionsRef,
		templateButtonRef,
		viewMode,
		contentClassificationMetadata,
		spaceKey,
		isUnpublishedDraft,
		showBlankPageQuickActions,
		isLivePage,
		version,
		isEditorEmpty,
		shouldRenderPreview,
		isEditorAnnotationProviderManagersEnabled,
		editorApi,
		contentType,
		editorActions,
		disabled,
		isTitleFocused,
		appearance,
		title,
		onTitleChange,
		cancelDebouncedSaveDraft,
		showSpinner,
		loadingPriority,
		isRovoButtonEnabled,
		editorSessionId,
		hasByLineContributors,
		hasByLineExtensions,
		hasReactions,
		hasPageComments,
		isEmbeddedConfluence,
		isTemplateBrowserEnabled,
		isTemplateBrowserPanelEnabled,
		isBlankPageQuickActionsEnabled,
		isSeamlessTransitionInProgress = false,
		isRovoLLMEnabled,
		customPlaceholderOverride,
		onCreateWithRovoClick,
		shouldShowCreateWithRovoExperience,
		onPromptMouseEnter,
		onPromptMouseLeave,
		onPromptClick,
		onPromptSubmit,
		onDismissCwr,
		hasSubmittedCwrPrompt,
		onInputEmpty,
		orgId,
		isInlineAIOpen,
	}) => {
		const titleAreaContentComponents = (
			<ReactionsProvider key="reactions-provider" contentId={contentId}>
				<TitleAreaContentComponentsWrapper
					isEditorEmpty={isEditorEmpty}
					contentId={contentId}
					cloudId={cloudId}
					focusEditor={focusEditor}
					onFocusChanged={onFocusChanged}
					debounceOnChange={debounceTitleChangeUpdates}
					userOffline={userOffline}
					templateSuggestionsRef={templateSuggestionsRef}
					templateButtonRef={templateButtonRef}
					linkCreation={linkCreation}
					hideSpinner={hideSpinner}
					viewMode={viewMode}
					contentClassificationMetadata={contentClassificationMetadata}
					version={version}
					editorApi={editorApi}
					editorActions={editorActions}
					editorSessionId={editorSessionId}
					isSeamlessTransitionInProgress={isSeamlessTransitionInProgress}
					hasByLineContributors={hasByLineContributors}
					hasByLineExtensions={hasByLineExtensions}
					hasReactions={hasReactions}
					customPlaceholderOverride={customPlaceholderOverride}
				/>
			</ReactionsProvider>
		);

		let blankPageQuickActionsTemplatePreview;
		const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

		// show BPQA full size template preview when we
		// are not previewing the editor, BPQA should show and it's either
		// an unpublished draft or a live page
		const shouldShowFullPageTemplatePreview =
			!shouldRenderPreview && showBlankPageQuickActions && (isUnpublishedDraft || isLivePage);

		if (shouldShowFullPageTemplatePreview) {
			blankPageQuickActionsTemplatePreview = (
				<BlankPageQuickActionsTemplatePreview
					key="blank-page-quick-actions-template-preview"
					spaceKey={spaceKey}
				/>
			);
		}

		const isEmbeddedBpqaEnabled = isEmbeddedConfluence
			? fg('confluence_embedded_decouple_template_browser_bpqa')
				? isBlankPageQuickActionsEnabled || isTemplateBrowserEnabled
				: isTemplateBrowserEnabled
			: true;

		const shouldRenderBPQA =
			isEmbeddedBpqaEnabled && !shouldRenderPreview && showBlankPageQuickActions && !isCompanyHub;

		const shouldRenderPageFooterComments = isEmbeddedConfluence ? hasPageComments : true;

		// The footer is pinned to the bottom on small live pages (see ObjectCommentContainer)
		// However, this can only happen on first paint in SPA (useEffect + the DOM aren't available in SSR), so the SSR'd version doesn't match the SPA version
		// Until we have a styling only fix, we should prevent rendering this in SSR (but keep preloading, etc to improve TTVC)
		const shouldRenderObjectCommentWrapper =
			fg('confluence_frontend_content_wrapper') &&
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			!process.env.REACT_SSR;

		const shouldShowEOPRecommendations =
			contentType === 'page' &&
			expValEquals('confluence_ai_related_content_for_live_pages', 'isEnabled', true) &&
			// Hide Related content / Send feedback (EOP recommendations) for embedded pages
			// when cc-maui-experiment is enabled.
			!(
				isEmbeddedConfluence &&
				(UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) ||
					fg('platform_native_embeds_rollout_non_maui_experience'))
			);

		return {
			before: [
				!isCompanyHub && titleAreaContentComponents,
				isLivePage && (
					<ImportedPagePolishBanner
						key="imported-page-polish-banner"
						editorActions={editorActions}
					/>
				),
				isAdminHubAIEnabled && (
					<EditorPluginAIWithFeedback
						key="editor-ai-feedback-collector"
						editorPluginAIFeedbackHandlers={editorPluginAIFeedbackHandlers}
					/>
				),
				isEditorAnnotationProviderManagersEnabled && (
					<EditorAnnotationProviderComponents key="editorAnnotationProviderComponents" />
				),
				<EditorForgeExtensionConfigHelperRenderer key="editorForgeExtensionConfigHelperRenderer" />,
				!isCompanyHub && (
					<TwoStageHydration
						key="object-sidebar-control-full-page-editor"
						name="object-sidebar-control-full-page-editor"
						// cc_editor_two_stage_hydration_removal - When cleaning up this experiment, also remove the related TwoStageHydration component
						enabled={
							!expValEquals(
								'cc_editor_two_stage_hydration_removal',
								'fullPageEditorComponentObjectSidebarControl',
								true,
							)
						}
						ssrPlaceholder
					>
						<ObjectSidebarControlFullPageEditor
							hasAiButton={isLivePage || isRovoButtonEnabled}
							hasAudioButton={getHasAudioButton({
								isAIEnabledForContent,
								isAIEarlyAccessEnabled,
							})}
							contentId={contentId}
							spaceKey={spaceKey}
							contentType={contentType}
							contentTitle={title}
							editorApi={editorApi}
							isUnpublishedDraft={isUnpublishedDraft}
							disabled={disabled}
							isEditorEmpty={isEditorEmpty}
						/>
					</TwoStageHydration>
				),
				shouldRenderBPQA &&
					shouldShowCreateWithRovoExperience &&
					isRovoLLMEnabled &&
					expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') && (
						<CreateWithRovoExperienceDismissText key="create-with-rovo-experience-dismiss-text" />
					),
				shouldRenderBPQA &&
					(!isRovoLLMEnabled || !isExperimentEnabled('cwr_blank_object_experiment')) && (
						<BlankPageQuickActions
							key="blank-page-quick-actions"
							editorActions={editorActions}
							disabled={disabled}
							isTitleFocused={isTitleFocused}
							focusEditor={focusEditor}
							appearance={appearance}
							spaceKey={spaceKey}
							isUnpublishedDraft={isUnpublishedDraft}
							title={title}
							onTitleChange={onTitleChange}
							contentId={contentId}
							cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
							showSpinner={showSpinner}
							hideSpinner={hideSpinner}
							isAIEnabled={isAdminHubAIEnabled}
							editorPresetAPI={editorApi}
							isTemplateBrowserEnabled={
								!isEmbeddedConfluence || isTemplateBrowserEnabled || isTemplateBrowserPanelEnabled
							}
						/>
					),
				// Variant2: show CreateWithRovoExperience in place of the old BPQA
				shouldRenderBPQA &&
					!hasSubmittedCwrPrompt &&
					isRovoLLMEnabled &&
					expValEquals('cwr_blank_object_experiment', 'cohort', 'variant2') && (
						<CreateWithRovoExperience
							key="create-with-rovo-experience-variant2"
							cloudId={cloudId}
							orgId={orgId}
							contentId={contentId}
							editorApi={editorApi}
							onPromptMouseEnter={onPromptMouseEnter}
							onPromptMouseLeave={onPromptMouseLeave}
							onPromptClick={onPromptClick}
							onPromptSubmit={onPromptSubmit}
							onInputEmpty={onInputEmpty}
							smartPromptsPosition="above"
							placement="bottom"
							contentType={isLivePage ? 'liveDoc' : 'page'}
							variant="variant2"
							isPublishedPage={!isUnpublishedDraft && !isLivePage}
						/>
					),
			],
			after: [
				shouldRenderBPQA &&
					!hasSubmittedCwrPrompt &&
					!isInlineAIOpen &&
					isRovoLLMEnabled &&
					expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') && (
						<BlankObjectExperienceButtons
							key="create-with-rovo-buttons"
							disabled={disabled}
							editorPresetAPI={editorApi}
							isTitleFocused={isTitleFocused}
							focusEditor={focusEditor}
							isAIEnabled={isAdminHubAIEnabled}
							isLivePage={isLivePage}
							isPublishedPage={!isUnpublishedDraft && !isLivePage}
							onCreateWithRovoClick={onCreateWithRovoClick}
						/>
					),
				// Variant1: show CreateWithRovoExperience after clicking the CWR button
				shouldRenderBPQA &&
					shouldShowCreateWithRovoExperience &&
					!hasSubmittedCwrPrompt &&
					isRovoLLMEnabled &&
					expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') && (
						<CreateWithRovoExperience
							key="create-with-rovo-experience"
							cloudId={cloudId}
							orgId={orgId}
							contentId={contentId}
							editorApi={editorApi}
							onPromptMouseEnter={onPromptMouseEnter}
							onPromptMouseLeave={onPromptMouseLeave}
							onPromptClick={onPromptClick}
							onPromptSubmit={onPromptSubmit}
							onDismiss={onDismissCwr}
							onInputEmpty={onInputEmpty}
							contentType={isLivePage ? 'liveDoc' : 'page'}
							variant="variant1"
						/>
					),
				blankPageQuickActionsTemplatePreview,
				shouldRenderPageFooterComments && !shouldRenderBPQA && isLivePage && (
					<ConditionalTwoStageHydration key="conditional-two-stage-hydration">
						{shouldShowEOPRecommendations && (
							<UFOLabel name="end-of-page-recommendation-loader">
								<EndOfPageRecommendationLoader
									entityType={contentType}
									entityId={contentId}
									spaceKey={spaceKey ?? ''}
									pageWidthType={`"max-width"`}
									isLivePage={isLivePage}
								/>
							</UFOLabel>
						)}
						{!fg('confluence_frontend_content_wrapper') && (
							<UFOLabel name="editor-footer-comments">
								<PageFooterComments
									key={contentId}
									contentId={contentId}
									loadingPriority={loadingPriority}
								/>
							</UFOLabel>
						)}
						{shouldRenderObjectCommentWrapper && (
							<>
								<UFOLabel name="editor-footer-comments">
									<ObjectCommentWrapper
										showGeneralComment
										showLabels
										key={`ObjectCommentWrapper-${contentId}`} // ensures safe re-render on page change
										hasReactions={hasReactions}
										editorIsUserOffline={userOffline}
									/>
								</UFOLabel>
								<StickyDraftCommentContainer />
							</>
						)}
					</ConditionalTwoStageHydration>
				),
			],
		};
	},
	isEqual,
);

const _getContextPanelComponent = memoize(() => {
	return <EditorContextPanelView />;
});

const _getEditorContextConnectionsPanel = memoize(
	({
		referentialityContext,
		connectionsPanelActiveNodeLocalId,
		setConnectionsPanelActiveNodeLocalId,
		closeConnectionsPanel,
	}) => {
		if (connectionsPanelActiveNodeLocalId && referentialityContext?.api) {
			return (
				<EditorContextConnectionsPanel
					api={referentialityContext.api}
					activeNodeLocalId={connectionsPanelActiveNodeLocalId}
					setActiveNodeLocalId={setConnectionsPanelActiveNodeLocalId}
					closeConnectionsPanel={closeConnectionsPanel}
				/>
			);
		}

		return null;
	},
);

const _getEditorContextSmartButtonConfigPanel = memoize(
	({ smartButtonActiveNodeLocalId, closeSmartButtonConfigPanel, fullPageEditorPresetAPI }) => {
		if (smartButtonActiveNodeLocalId) {
			return (
				<EditorContextSmartButtonConfigPanel
					localId={smartButtonActiveNodeLocalId}
					closeSelf={closeSmartButtonConfigPanel}
					editorApi={fullPageEditorPresetAPI}
				/>
			);
		}
		return null;
	},
	isEqual,
);

type FeatureFlags = { [key: string]: string | number | boolean };

type PlaceholderTextParam = {
	isAIEnabledForContent: boolean;
	featureFlags: FeatureFlags;
};

// IMPORTANT: If you change the code below, please also update the
// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/placeholderPluginOptions/placeholderPluginOptions.ts
const _getPlaceholderText = memoize((placeholderTextParam: PlaceholderTextParam) => {
	const isAIEnabledForContent = placeholderTextParam.isAIEnabledForContent;

	if (isAIEnabledForContent && fg('aifc_create_enabled')) {
		return i18n.editorEmptyDocumentSpaceShortcutPlaceholder;
	}

	if (isAIEnabledForContent) {
		return i18n.editorEmptyDocumentPlaceholderAI;
	}

	if (editorExperiment('platform_editor_controls', 'variant1')) {
		return i18n.editorEmptyDocumentPlaceholder;
	}

	const key = `${isAIEnabledForContent ? 'AI' : 'noAI'}`;

	const placeholders = {
		AI: {
			//  Placeholder text when AI is enabled
			id: AI_PLACEHOLDER_TEXT_ID,
			defaultMessage: AI_PLACEHOLDER_DEFAULT_MESSAGE,
		},
		noAI: {
			//  Placeholder text when AI is not enabled
			id: EASY_MENTIONS_PLACEHOLDER_TEXT_ID,
			defaultMessage: EASY_MENTIONS_PLACEHOLDER_DEFAULT_MESSAGE,
		},
	};

	return placeholders[key];
});

const withEditorContext = (component) =>
	withSmartCardProviderContext(withRevertContext(withEditorModalContext(component)));
const getContentAppearance = memoize((appearance, draftAppearance): FullPageAppearances => {
	// if the appearance is default value (version 0), reference the draftAppearance if it exists; otherwise, use appearance
	const contentAppearance =
		appearance.version === 0 && draftAppearance ? draftAppearance : appearance.appearance;

	return getContentAppearanceForAtlaskit(contentAppearance);
});

// If updating please update in DiffEditor.tsx as well
const _createLCMExtensionProvider = async (
	outerEditorView: EditorView | undefined,
	outerEditorApi: PublicPluginAPI<[ExtensionPlugin, LegacyContentMacroPlugin]> | undefined,
	propOverrides?: { contentMode?: 'standard' | 'compact' | undefined },
) => {
	const { createManifest } = await import(
		/* webpackChunkName: "loadable-legacy-content-macro" */ '@confluence/experiment-legacy-content-macro'
	);

	return new DefaultExtensionProvider([
		createManifest(outerEditorView, outerEditorApi, propOverrides) as unknown as ExtensionManifest,
	]);
};

type FullPageEditorComponentImplProps = FullPageEditorComponentProps &
	WithFlagsProps &
	WithDialogsProps &
	WithDocChangeContextProps;

const FullPageEditorComponentImpl = (props: FullPageEditorComponentImplProps) => {
	// Should be always the first statement in the component
	const firstRenderStartTimestampRef = useRef(performance.now());

	const {
		// Props from Loader
		contentId,
		spaceKey,
		collabEditProvider,
		collabEdit,
		collabEditState,
		heartbeatData,
		contentType,
		disabled,
		setForceDisabled,
		internetDisconnected,
		forceDisabled,
		draftShareId,
		refetchNativeCollab,
		isFirstParticipant,
		isSingleUserSession,
		isUnpublishedDraft,
		isOverviewPage,
		onTitleChange,
		title,
		version,
		viewMode,
		onPublishUpdateTitleInPageTree,
		optimisticDeleteItemInTree,
		customPerfData,
		// Props from editor-core
		editorActions,
		// Context Provider for macro
		extensionContextProvider,
		flags,
		dialogs,
		handleDocChange,
		pageTreeStateUpdater,
		/**
		 * defaults to true
		 * used by live pages as live pages render editor on view only mode
		 */
		hasEditPermission = true,
		createdDate,
		onDraftSyncSuccess,
		loadingPriority,
		isFastLoadedLiveDoc,
		hasByLineContributors = true,
		hasByLineExtensions = true,
		hasReactions = true,
		hasPageComments = true,
		hasInlineComments = true,
	} = props;

	const [{ fullPageEditorPresetAPI }, { setEditorApi, resetEditorApi }] = useFullPageEditorApi();

	const { detectedLinks, newLinkTypes, newLinks, shouldShowSpotlight, closeSpotlight } =
		useIntegrationLinkSpotlightForFullPageEditor(fullPageEditorPresetAPI);

	// Ensures editorApi sweet state is cleared on unmount
	useEffect(() => {
		return () => {
			resetEditorApi();
		};
	}, [resetEditorApi]);

	// Recommended way of accessing collabEditProvider without promise wrapper.
	const [{ collabEditProvider: extractedCollabEditProvider }] = useNativeCollabState();
	const [{ collabEditConnected }] = useNativeCollabConnected();
	const { isDraftLockedForEditing } = useApprovals();
	const spaceId = useSpaceId();

	const editorAPIRef = useRef(fullPageEditorPresetAPI);
	useEffect(() => {
		editorAPIRef.current = fullPageEditorPresetAPI;
	}, [fullPageEditorPresetAPI]);

	const snippetExtensionList = useSnippetExtension({
		contentId,
		spaceId: spaceId ?? '',
		spaceKey,
		editorAPIRef,
	});
	const { triggerAnalyticsEvent } = useEditorAnalyticsEvent({
		contentId,
		contentType,
		subject: 'editPage',
		source: ANALYTICS_SOURCE,
		version,
		collabService: 'ncs',
	});

	const { requestToEditContent, hasRequestedEditPermission } = useRequestToEditContent({
		contentId,
		flags,
		triggerAnalyticsEvent,
	});

	const isClassicMentionsNotificationsEnrolled = useIsMentionOnRestrictedPageEligible();

	const { setEditorAnnotations } = useEditorAnnotationsActions();

	const isDocumentActive = useDocumentActive();

	const { isSmartButtonAllowed } = useSmartButtonPremiumGate();
	const readFragmentInterop = useReadFragmentInterop({
		fragmentSpec: graphql`
			fragment FullPageEditorComponentReadFragmentWithAppKey on MacroConnection {
				nodes {
					macroId
					contentId
					renderedMacro(mode: EDITOR) {
						appKey
						value
						macroRenderedRepresentation
						macroBodyStorage
						webResource {
							contexts
							keys
							superbatch {
								metatags
							}
							tags {
								css
								data
								js
							}
							uris {
								css
								data
								js
							}
						}
						mediaToken {
							token
						}
					}
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		`,
	});
	const { updateTitle: updateContentTypesTitle } = useContentTypesTitleUpdate(
		contentId,
		title || '',
	);

	const {
		collabEditInitialized,
		collabEditError,
		collabEditInternetLost,
		collabEditConnectionTimedOut,
	} = collabEditState;
	const intl = useIntl();
	const {
		cloudId,
		userId,
		featureFlags,
		isAdminHubAIEnabled,
		edition,
		orgId,
		environment,
		isAIEarlyAccessEnabled,
		isRovoLLMEnabled,
	} = useSessionData();
	const { push, overrideUFORouteName } = useRouteActions();
	const linkCreation = useLinkCreationQueryParam();
	const focusedCommentId = useFocusedCommentIdQueryParam();

	const replyToCommentId = useReplyToCommentIdQueryParam();
	const focusedMentionId = useFocusedMentionIdQueryParam();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { triggerSaveIndicator } = useContext(TriggerSaveIndicatorContext);

	const isEditorEmpty: boolean =
		useSharedPluginStateWithSelector(
			fullPageEditorPresetAPI,
			// @ts-ignore @ts-expect-error
			['empty'],
			// @ts-ignore @ts-expect-error
			(states) => !!states?.emptyState?.editorIsEmpty,
		) ?? editorActions?.isDocumentEmpty();

	const showBlankPageQuickActions = useShouldShowBlankPageQuickActions({
		contentType,
		collabEditInitialized,
		isEditorEmpty,
		viewMode,
	});

	const isInlineAIOpen: boolean =
		useSharedPluginStateWithSelector(
			fullPageEditorPresetAPI,
			// @ts-ignore @ts-expect-error
			['aiExperience'],
			// @ts-ignore @ts-expect-error
			(states) => !!states?.aiExperienceState?.aiDecorationExperiencePluginState?.isRunning,
		) ?? false;

	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();

	const isLivePage = useIsCurrentPageLive();
	const versionOverride = useVersionOverrideQueryParam();
	const pageVersion = usePageVersionQueryParam();
	const isVersionHistoryView = versionOverride != null || pageVersion != null;
	const isArchived = getCachedPageData(contentId)?.isArchived ?? false;
	const remixDisabledReason = isArchived
		? ('archived' as const)
		: isVersionHistoryView
			? ('version-history' as const)
			: undefined;

	// Uses optional chaining (not destructuring) because the module may return
	// undefined in test environments where it is not mocked.
	const nativeEmbedClient = usePageNativeEmbedClient();
	const sendNativeEmbedChildCommand =
		fg('cc-maui-phase-2') || fg('platform_native_embeds_rollout_non_maui_experience')
			? nativeEmbedClient?.sendChildCommand
			: undefined;

	const templateSuggestionsRef = useRef<HTMLDivElement | null>(null);
	const templateButtonRef = useRef<HTMLElement | null>(null);

	const [{ editSource }] = useEditSource();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	// State for Create with Rovo experience
	const [shouldShowCreateWithRovoExperience, setShouldShowCreateWithRovoExperience] =
		useState<boolean>(false);
	const [hasSubmittedCwrPrompt, setHasSubmittedCwrPrompt] = useState<boolean>(false);
	const [customPlaceholderOverride, setCustomPlaceholderOverride] = useState<string | undefined>(
		undefined,
	);

	// Handler for Create with Rovo button click
	const handleCreateWithRovo = useCallback(() => {
		setShouldShowCreateWithRovoExperience(true);
	}, []);

	// open "Create with Rovo" modal via quick insert
	const openSmartCreationModalFromQuickInsert =
		useOpenSmartCreationModalFromQuickInsert(editorActions);

	// Track whether a prompt has been clicked so hover-off doesn't clear it
	const isPromptSelectedRef = useRef(false);

	// Handlers for smart prompt hover to update title placeholder
	const onPromptMouseEnter = useCallback((prompt: SmartPrompt) => {
		if (!isPromptSelectedRef.current) {
			setCustomPlaceholderOverride(prompt.label);
		}
	}, []);

	const onPromptMouseLeave = useCallback(() => {
		if (!isPromptSelectedRef.current) {
			setCustomPlaceholderOverride(undefined);
		}
	}, []);

	// When a prompt is clicked, persist its label as the title placeholder
	const onPromptClick = useCallback((prompt: SmartPrompt) => {
		isPromptSelectedRef.current = true;
		setCustomPlaceholderOverride(prompt.label);
	}, []);

	// When a prompt is submitted, hide the CWR experience and the buttons.
	const onPromptSubmit = useCallback(() => {
		setShouldShowCreateWithRovoExperience(false);
		setHasSubmittedCwrPrompt(true);
	}, []);

	// Open Rovo chat sidebar when CWR streaming completes.
	const { toggleConversationAssistant, mode: conversationAssistantMode } =
		useToggleConversationAssistant();

	const onCwrStreamEnd = useCallback(() => {
		if (
			hasSubmittedCwrPrompt &&
			conversationAssistantMode !== ConversationAssistantUIModes.SIDEBAR &&
			(expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') ||
				expValEquals('cwr_blank_object_experiment', 'cohort', 'variant2'))
		) {
			toggleConversationAssistant('sidebar');
		}
	}, [hasSubmittedCwrPrompt, toggleConversationAssistant, conversationAssistantMode]);

	// Reset hasSubmittedCwrPrompt when the editor transitions from non-empty → empty
	// (user deleted Rovo-generated content), so BOE buttons reappear.
	const prevIsEditorEmptyRef = useRef(isEditorEmpty);
	useEffect(() => {
		const wasEmpty = prevIsEditorEmptyRef.current;
		prevIsEditorEmptyRef.current = isEditorEmpty;
		if (!wasEmpty && isEditorEmpty && hasSubmittedCwrPrompt) {
			setHasSubmittedCwrPrompt(false);
		}
	}, [isEditorEmpty, hasSubmittedCwrPrompt]);

	// When the user clicks outside the CWR experience, dismiss it and reset the title placeholder
	const onDismissCwr = useCallback(() => {
		setShouldShowCreateWithRovoExperience(false);
		setCustomPlaceholderOverride(undefined);
		isPromptSelectedRef.current = false;
	}, []);

	// When the user clears the prompt input, reset the title placeholder
	const onInputEmpty = useCallback(() => {
		isPromptSelectedRef.current = false;
		setCustomPlaceholderOverride(undefined);
	}, []);

	const handleGqlError = useCallback(
		(error: Error, isCommentNotFound: boolean = false) => {
			experienceTracker.stopOnError({
				name: VIEW_INLINE_COMMENT_EXPERIENCE,
				error,
				attributes: {
					mode: isLivePage ? InlineCommentMode.LIVE : InlineCommentMode.EDIT,
					stopPoint: 'full-page-editor/FullPageEditorComponent',
					commentNotFound: isCommentNotFound,
				},
			});
		},
		[experienceTracker, isLivePage],
	);

	// Note that this will detect missing inline and footer comments because the query will
	// find either type of comment - so if the comment doesn't exist, we will get the "not
	// found" error
	const handleUseFocusedCommentGQLError = useCallback(
		(error: Error) => {
			const isCommentNotFound = error ? isCommentNotFoundError(error.message) : false;
			if (flags && isCommentNotFound) {
				const commentId = focusedCommentId || replyToCommentId;
				const FULLPAGEEDITOR_COMMMENT_LINK_NOT_FOUND_FLAG_ID = `fullpageeditor-comment-link-not-found-${commentId}`;
				void flags.showWarningFlag({
					id: FULLPAGEEDITOR_COMMMENT_LINK_NOT_FOUND_FLAG_ID,
					title: <FormattedMessage {...i18n.commentLinkNotFoundFlagTitle} />,
					description: <FormattedMessage {...i18n.commentLinkNotFoundFlagDescription} />,
				});
			}
			handleGqlError(error, isCommentNotFound);
		},
		[flags, focusedCommentId, handleGqlError, replyToCommentId],
	);

	// === Editor State ===
	const [shouldRenderPreview, setShouldRenderPreview] = useState<boolean>(false);
	const [previewAsBlog, setPreviewAsBlog] = useState<boolean>(false);
	const [stagedContentId, setStagedContentId] = useState<string | undefined>();
	const [stagedSpaceKey, setStagedSpaceKey] = useState<string | undefined>();
	const [proseMirrorRenderedData, setProseMirrorRenderedData] =
		useState<ProseMirrorRenderedDataType | null>(null);
	const [checkIfConditionPreventingPublish, setCheckIfConditionPreventingPublish] =
		useState<() => Promise<boolean>>();
	const assignConditionalFunction = useCallback(
		(conditionalFunction: any) => setCheckIfConditionPreventingPublish(() => conditionalFunction),
		[],
	);

	const { updateUnresolvedInlineComment } = useInlineCommentsActions();

	// importing the comments data states here as a temporary workaround to fix circular dependency, we need these states to handle dangling and unread from unread-comments package
	// unread-comments package is using comments-data package so we can't import comments-data into unread-comments package
	const { setOrderedActiveAnnotationIdList } = useCommentsDataActions();
	const { setCurrentView } = useCommentsPanelActions();

	const handleCollabError = useHandleCollabError(flags);
	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState(
		Object.values(EDITOR_ONBOARDING_STATE_KEYS),
		!fg('cc_onboarding_experience') || version !== 1,
	);

	// when a user is eligible for contextual invite nudge, tag them before they publish
	void useContextualInviteNudge();

	const { editorOnboardingEligiblity, recentSpotlightCompleted } =
		deserializeState(onboardingStateData);

	const shouldShowRecentSpotlight =
		!onboardingStateLoading && editorOnboardingEligiblity && !recentSpotlightCompleted; // Makes sure recentSpotlightCompleted is not undefined

	const { onEditorReady, startingLinks } = useInviteFromLinkExperimentEditor();

	const { handleSave: handleSaveThirdPartyLinks } = useDetectPastedThirdPartyLinksForFullPageEditor(
		fullPageEditorPresetAPI ?? undefined,
		isLivePage,
	);

	// === Editor common functionalities and providers ===
	const { collabLimitReached, heartbeatError, stopHeartbeatCallback, editorSessionId } =
		heartbeatData;

	// Preloading native collab data for faster refetch when publishing
	const { refetchNativeCollab: refetchNativeCollabWithPreloading, preloadNativeCollabRefetch } =
		useRefetchNativeCollab(refetchNativeCollab);

	// Edit page draft syncing
	const {
		cancelDebouncedSaveDraft,
		triggerSaveDraft,
		navigateOnClose,
		onPublish,
		isPublishingContent,
		saveDraft,
		saveDraftError,
	} = useDraftSyncAndPublish({
		contentId,
		contentType,
		refetchNativeCollab: expValEquals(
			'confluence_publish_ivc_native_collab_query',
			'isEnabled',
			true,
		)
			? refetchNativeCollabWithPreloading
			: refetchNativeCollab,
		isFirstParticipant,
		isUnpublishedDraft,
		spaceKey,
		title,
		version,
		onCollabError: handleCollabError,
		editorActions,
		checkIfConditionPreventingPublish,
		flags,
		dialogs,
		editorPresetAPI: fullPageEditorPresetAPI,
		editorSessionId,
		onPublishUpdateTitleInPageTree,
		optimisticDeleteItemInTree,
		startingLinks,
		handleSaveThirdPartyLinks,
		sendNativeEmbedChildCommand,
	});

	// Live doc draft syncing
	const { draftSyncActionError } = useEditorDraftSyncAction({
		contentId,
		editorPresetAPI: fullPageEditorPresetAPI,
		editorActions,
		onDraftSyncSuccess,
		isFirstParticipant,
		flags,
	});

	const hasUnsavedChanges = useHasUnsavedChanges({
		contentId,
		isPublishingContent,
		isFirstParticipant,
		editorPresetAPI: fullPageEditorPresetAPI,
	});
	const printLockout = usePrintIntercept({
		enabled: expValEquals('afm-enable-command-p-reroute', 'isEnabled', true),
		shouldBlockPrint: () => hasUnsavedChanges,
	});

	const { isTransitionPreloading: isLiveDocSeamlessTransitionPreloading } =
		useLiveDocTransitionCallback({
			contentId,
			collabEditProvider: extractedCollabEditProvider,
			isPublishingContent,
			isFirstParticipant,
			editorPresetAPI: fullPageEditorPresetAPI,
			isLivePage,
			hasEditPermission,
			isFastLoadedLiveDoc,
		});

	const [isLiveDocSeamlessTransitioning, setIsLiveDocSeamlessTransitioning] =
		useState<boolean>(false);

	useEffect(() => {
		let newTransitioningState: boolean | undefined;
		if (isLivePage) {
			if (!isLiveDocSeamlessTransitioning && isLiveDocSeamlessTransitionPreloading) {
				newTransitioningState = true;
			} else if (
				isLiveDocSeamlessTransitioning &&
				!disabled &&
				!isLiveDocSeamlessTransitionPreloading
			) {
				newTransitioningState = false;
			}
		} else if (isLiveDocSeamlessTransitioning) {
			newTransitioningState = false;
		}
		if (
			newTransitioningState !== undefined &&
			newTransitioningState !== isLiveDocSeamlessTransitioning
		) {
			setIsLiveDocSeamlessTransitioning(newTransitioningState);
		}
	}, [
		contentId,
		disabled,
		isLiveDocSeamlessTransitionPreloading,
		isLivePage,
		isLiveDocSeamlessTransitioning,
	]);

	useEffect(() => {
		if (
			!isLivePage &&
			isOverviewPage &&
			// exposure will only fire for overview pages
			expValEquals('cc_editor_ttvc_split_space_overview_edit_page', 'isEnabled', true)
		) {
			overrideUFORouteName('space-overview-edit-page');
		}
	}, [overrideUFORouteName, isLivePage, isOverviewPage]);

	const [isAutoConverted] = useAutoConvertState();

	const { draftTitle, draftBody, draftAppearance } = useInjectDraftContent({
		contentId,
		draftShareId,
		collabEditInitialized,
		editorActions,
		isUnrecoverableCollabEditError: !!collabEditError && isUnrecoverableError(collabEditError),
	});

	// Get cached title from Apollo cache (e.g., from page tree) to use as initial value
	// while waiting for collab to initialize. This prevents the "Give this page a title"
	// placeholder from flashing during transitional loads.
	const cachedTitle = useMemo(() => {
		// Skip cache lookup if we already have a title from collab or draft
		if (title || draftTitle) {
			return null;
		}
		if (expValEquals('confluence_load_editor_title_on_transition', 'isEnabled', true)) {
			const { title: cached } = getCachedPageTitle(contentId);
			// For cwr_blank_object_experiment variant1 and variant2 on live docs,
			// do not use the cached title if it matches the auto-generated default format
			// "Untitled live doc YYYY-MM-DD" and the editor is empty (BOE is visible).
			if (
				isLivePage &&
				isRovoLLMEnabled &&
				isEditorEmpty &&
				isDefaultLiveDocTitle(cached) &&
				(expValEquals('cwr_blank_object_experiment', 'cohort', 'variant1') ||
					expValEquals('cwr_blank_object_experiment', 'cohort', 'variant2'))
			) {
				return null;
			}
			return cached ?? null;
		}
		return null;
	}, [contentId, title, draftTitle, isLivePage, isRovoLLMEnabled, isEditorEmpty]);

	const {
		connectionsPanelActiveNodeLocalId,
		setConnectionsPanelActiveNodeLocalId,
		openConnectionsPanel,
		closeConnectionsPanel,
	} = useConnectionsPanel();

	const {
		activeNodeLocalId,
		openConfigPanel,
		closeConfigPanel,
		upsellModalSource,
		setUpsellModalSource,
	} = useCustomExtensionConfigPanel(editorActions);

	const { smartButtonActiveNodeLocalId, openSmartButtonConfigPanel, closeSmartButtonConfigPanel } =
		useSmartButtonConfigPanel();

	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	const { optInStatus, triggerOptInFlow, AIOptInModal } = useEditorAIOptIn({
		isAdminHubAIEnabled,
		isAIEnabledForContent,
		edition,
		orgId,
		environment,
	});

	const aiGlobalOptIn = useMemo(
		() => ({
			status: optInStatus,
			triggerOptInFlow,
		}),
		[optInStatus, triggerOptInFlow],
	);

	const { editorPluginAIFeedbackHandlers } = useEditorPluginAIFeedbackHandlers();
	const { onAIProviderChanged } = useEditorPluginAICollabConnector();

	const getChannelVariables = useGetChannelVariables(cloudId, userId || 'anonymous', orgId);

	// Build media viewer extensions to add the comments button to the preview header
	const mediaViewerExtensions = useMediaCommentExtensions({
		isViewPage: false,
		analyticsSource: isLivePage ? 'livePage' : 'editPageScreen',
		flags,
	});

	const fallbackMediaNameFetcher = useMemo(
		() => createFallbackMediaNameFetcher(contentId),
		[contentId],
	);

	const mediaOptions = useMediaOptions({
		contentId,
		contentType,
		flags,
		viewMode,
		editorAPI: fullPageEditorPresetAPI ?? undefined,
		mediaViewerExtensions,
		fallbackMediaNameFetcher: expVal('platform_editor_media_name_fallback', 'isEnabled', false)
			? fallbackMediaNameFetcher
			: undefined,
	});

	const handleFeedbackClicked = useCallback(
		(props) => {
			editorPluginAIFeedbackHandlers.current.handleFeedbackClicked(props);
		},
		[editorPluginAIFeedbackHandlers],
	);

	const handleReviewStateEntered = useCallback(() => {
		editorPluginAIFeedbackHandlers.current.handleReviewStateEntered();
	}, [editorPluginAIFeedbackHandlers]);

	const handleExperienceEnded = useCallback(() => {
		editorPluginAIFeedbackHandlers.current.handleExperienceEnded();
	}, [editorPluginAIFeedbackHandlers]);

	const editorPluginAIProviders = useMemo(
		() => ({
			mediaProvider: mediaOptions.provider!,
		}),
		[mediaOptions.provider],
	);

	const { isCommentsPanelShown, showCommentsPanel } = useShowCommentsPanel();

	const onShowSuggestionsSidebar = useCallback(() => {
		showCommentsPanel({ openWithTab: SidebarTabValues.SUGGESTIONS });
	}, [showCommentsPanel]);

	const publishRovo = usePublish('ai-mate');

	const onOpenRovoChatWithSkill = useCallback(() => {
		publishRovo({
			type: 'chat-new',
			source: 'confluence-suggest-edits-skill',
			openChat: true,
			data: {
				prompt: intl.formatMessage(messages.suggestEditsSkillPrompt, {
					skillCommand: '/suggest-edits',
				}),
				isPromptPlaceholder: true,
				placeholderType: 'skill',
				dialogues: [],
				mode: {
					thinkDeeperEnabled: true,
					useCurrentPageContext: true,
				},
			},
		});
	}, [publishRovo, intl]);

	const handleAnnotationsRemovedByAI = useStableCallback((ids: string[]) => {
		if (!ids?.length) {
			return;
		}

		if (ids.length) {
			void flags?.showInfoFlag({
				id: ids[0],
				title: intl.formatMessage(i18n.commentRemovedByRovoTitle, {
					count: ids.length,
				}),
				description: intl.formatMessage(i18n.commentRemovedByRovoDescription, {
					count: ids.length,
				}),
				isAutoDismiss: true,
				actions: [
					{
						content: intl.formatMessage(i18n.commentRemovedByRovoViewCommentCTA, {
							count: ids.length,
						}),
						onClick: () => {
							const openWithView = fg('confluence_frontend_new_dangling_comments_ux')
								? ViewValues.DELETED_CONTENT
								: ViewValues.OPEN;

							if (!isCommentsPanelShown) {
								showCommentsPanel({ openWithView });
							} else {
								setCurrentView(openWithView);
							}

							void flags.hideFlag(flags.state.flags[0]?.id);
						},
					},
				],
			});
		}
	});

	const editorPluginAIOptions = useEditorPluginAIOptions({
		aiGlobalOptIn,
		appearance: 'full-page',
		onFeedbackClicked: handleFeedbackClicked,
		onReviewStateEntered: handleReviewStateEntered,
		onExperienceComplete: handleExperienceEnded,
		onAIProviderChanged,
		getChannelVariables,
		providers: editorPluginAIProviders,
		contentId,
		onAnnotationsRemoved: handleAnnotationsRemovedByAI,
		usePlatformPrompt: fg('aifc_create_enabled'),
	});

	const {
		isTitleFocused,
		shouldFocusEditor,
		debounceTitleChangeUpdates,
		focusEditor,
		onFocusChanged,
	} = useEditorTitleFocus({
		contentType,
		editorActions,
		isUnpublishedDraft,
	});

	const updateUnresolvedInlineCommentFn = useUpdateAnnotations([
		updateUnresolvedInlineComment,
		setOrderedActiveAnnotationIdList,
	]);
	const annotationProviderOptions = useEditorAnnotationProviderOptions({
		pageId: contentId,
		triggerAnalyticsEvent,
		editorActions,
		updateUnresolvedInlineComment: updateUnresolvedInlineCommentFn,
	});
	const activityProvider = useActivityProvider();
	const mentionProvider = useMentionProvider(spaceId, undefined, focusedMentionId);
	const profilecardProvider = useProfilecardProvider({
		push,
		cloudId,
		featureFlags,
		userId,
	});
	const mentionProps = useMemo(() => {
		return {
			profilecardProvider,
		};
	}, [profilecardProvider]);
	const searchProvider = useSearchProvider();
	const findReplaceOptions = getFindReplaceOptions();
	const taskDecisionProvider = getTaskDecisionProvider();
	const autoformattingProvider = useAutoformattingProvider({
		contentId,
		spaceKey,
		source: ANALYTICS_SOURCE,
	});
	const emojiProvider = getEmojiProvider(EmojiResource, {
		cloudId,
		userId: userId || undefined,
		allowAnonymousAccess: true,
	});

	const contextIdentifierProvider = useContextIdentifierProvider(spaceId, contentId);

	const syncedBlockProvider = useMemoizedSyncedBlockProviderForEditor({
		contentId,
		contentType,
		focusedMentionId,
		spaceId,
		editorActions,
	});
	const matchRoute = useMatchRoute();
	const syncedBlockEventHandlers = useMemo(() => {
		return createSmartCardEventHandler({ matchRoute, push }, true);
	}, [matchRoute, push]);

	const { showMacroFallbackFlag } = useMacroFallbackFlag(flags, isLivePage);
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	const { mode: editorContentMode } = useEditorContentModeValue({ contentId });

	const allowCreateWithRovoExtension =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		isRovoButtonEnabled &&
		contentType === 'page' &&
		!isEmbeddedConfluence &&
		fg('aifc_create_enabled') &&
		fg('cc_fd_cwr_quick_insert_gate');

	// Extension V2 API props
	const { extensionOptions, rendererExtensionOptions } = useExtensionOptions({
		contentId,
		contentTitle: title ?? undefined,
		contentType,
		contentVersion: version,
		contentMode: editorContentMode,
		editorActions,
		mentionProvider,
		spaceKey,
		spaceId,
		extensionContextProvider,
		openConnectionsPanel,
		openConfigPanel,
		openSmartButtonConfigPanel,
		onCompleteCallback: showMacroFallbackFlag,
		isLivePage,
		allowCreateDatabaseExtension: true,
		allowDatabaseQuickInsert: true,
		allowCreateWhiteboardExtension: true,
		allowCreateWithRovoExtension,
		openSmartCreationModal: openSmartCreationModalFromQuickInsert,
		flags,
		aiOptInStatus: optInStatus,
		editorAPI: fullPageEditorPresetAPI ?? undefined,
		setUpsellModalSource,
		isFullPageEditor: true,
		allowCompanyHubExclusiveExtensions: isCompanyHub,
		allowLegacyMacros: true,
		isCollabEditForLiveUpdatesEnabled: true,
		pageTreeStateUpdater,
	});

	const extensionProvidersWithLCM = useMemo(() => {
		const outerEditorView = editorActions._privateGetEditorView();

		const LCMExtension = _createLCMExtensionProvider(outerEditorView, fullPageEditorPresetAPI, {
			contentMode: editorContentMode,
		});

		return [...extensionOptions.extensionProviders, LCMExtension];
	}, [
		editorActions,
		fullPageEditorPresetAPI,
		extensionOptions.extensionProviders,
		editorContentMode,
	]);

	const { showRevertDialog } = useEditorRevertDialogContext();
	const { showSpinner, hideSpinner, shouldShowSpinner } = useSpinner();

	const editorAnnotationsRef = useRef<string[]>([]);
	const annotationStateRef = useRef<InlineCommentPluginState | undefined>();
	const { removeCommentQueryParams } = useInlineCommentQueryParams();

	const { addCommentInCache, deleteCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true,
		},
	});

	const { retrieveEditorTitleAndContent } = useRetrieveEditorTitleAndContent({
		onCollabError: handleCollabError,
		editorActions,
	});

	// TODO: add missing configs/providers (possibly not all):
	// presenceProvider
	// legacyImageUploadProvider
	// placeholderBracketHint
	// allowAnalyticsGASV3
	// onDestroy

	const wrapperProps: EditorWrapperProps = {
		attribution: Attribution.PAGE_EXTENSIONS,
		collabEditState,
		contentId,
		forceDisableEditor: setForceDisabled,
		isUnpublishedDraft,
		contentType,
		editorActions,
		heartbeatError,
		saveDraftError: isLivePage ? draftSyncActionError : saveDraftError,
		stopHeartbeatCallback,
		isLivePage,
		editorDisabled: disabled,
	};

	const performanceTrackingOptions = undefined;

	const editorFeatureFlags = useEditorFeatureFlags();
	const editorExperiments = useEditorFullPageExperiments();

	// Fetch content's classification data

	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: isLivePage ? 'current-and-draft' : 'draft-only',
	});

	useEffect(() => {
		// Wait for both collab to be initialized AND ProseMirror DOM to be rendered
		// before attempting to scroll. If we scroll before ProseMirror renders,
		// querySelectorAll('.ProseMirror > *') returns empty and scroll is a no-op.
		if (collabEditInitialized && !disabled && proseMirrorRenderedData !== null) {
			scrollToLastViewedPosition(contentId);
		}
	}, [collabEditInitialized, disabled, contentId, proseMirrorRenderedData]);

	useEffect(() => {
		// DTR-1401-CFE
		// HOT-103036

		// If a blogpost is selected from in-editor templates panel, any preset restrictions are lost.
		// This lets the user know to update their blog restrictions.
		const showBlogpostAccessFlag = localStorage.getItem(keys.BLOGPOST_PERMISSIONS_NOTICE);

		if (flags && showBlogpostAccessFlag === 'true') {
			void flags.showInfoFlag({
				title: <FormattedMessage {...i18n.blogpostPermissionsNotice} />,
			});
			localStorage.removeItem(keys.BLOGPOST_PERMISSIONS_NOTICE);
		}
	}, [flags]);

	useEffect(() => {
		document.addEventListener('keydown', () => measureTimeToEdit(triggerAnalyticsEvent), {
			once: true,
		});
	}, [triggerAnalyticsEvent]);

	const onProseMirrorRendered = (proseMirrorRenderedAttributes) => {
		proseMirrorRenderedAttributes = proseMirrorRenderedAttributes || {};
		// "distortedDuration" gets a true value if page was loaded in the background
		// in a non-visible tab which can lead to inaccurate duration numbers,
		// this can help us to capture only legitimate durations in our metric dashboards.
		// See https://bitbucket.org/atlassian/atlassian-frontend/src/master/packages/editor/editor-common/src/utils/performance/measure-render.ts
		const {
			proseMirrorRenderedFMP,
			proseMirrorRenderedTimestamp,
			startTime,
			duration,
			nodes,
			nodeSize,
			nodeSizeBucket,
			totalNodes,
			nodesInViewport,
			distortedDuration,
		} = proseMirrorRenderedAttributes;
		const topLevelNodeCount = fg('analytics_for_large_candidate_pages')
			? editorActions._privateGetEditorView()?.state.doc.childCount
			: undefined;
		setProseMirrorRenderedData({
			proseMirrorRenderedFMP,
			proseMirrorRenderedTimestamp,
			startTime,
			duration,
			nodes,
			nodeSize,
			totalNodes,
			topLevelNodeCount,
			nodesInViewport,
			distortedDuration,
		});
		if (expValEquals('cc_editor_interactivity_monitoring', 'isEnabled', true)) {
			insm.endHeavyTask('PageLoad');
		}
		if (expValEquals('cc_editor_insm_doc_size_stats', 'isEnabled', true)) {
			insm.session?.addProperties({
				'initial:totalNodes': totalNodes,
				'initial:nodeSize': nodeSize,
				'initial:nodes': nodes,
				'initial:nodesInViewport': nodesInViewport,
				'initial:renderDuration': duration,
				'initial:nodeSizeBucket': nodeSizeBucket,
			});
		}
	};
	useEffect(() => {
		waitForGlobal(
			{
				listener: 'FullPageEditorComponent',
				globalProperty: 'AJS.bind',
			},
			() => {
				(window as any).AJS.bind('createGASv3Event', (_, data) => {
					createAnalyticsEvent(data).fire();
				});
			},
		);

		return () => {
			waitForGlobal(
				{
					listener: 'FullPageEditorComponent',
					globalProperty: 'AJS.unbind',
				},
				() => {
					(window as any).AJS.unbind('createGASv3Event');
				},
			);
		};
	}, [createAnalyticsEvent]);

	useEffect(() => {
		isAutoConverted &&
			sendEditorConversionAnalyticsPageEvent(
				createAnalyticsEvent,
				{
					pageAction: AutoConvertEvent.EDITED,
					source: 'FullPageEditorComponent-Next',
				},
				AutoConvertActionSubject.AUTO_CONVERSION,
			);
	}, [isAutoConverted, createAnalyticsEvent]);

	const { setOptimisticTitle, optimisticTitle } = useOptimisticTitleChange(contentId, title);
	const updateTimestampCache = useUpdateLastEditedTimestampVersionCache();

	const userOffline = collabEditInternetLost || collabEditConnectionTimedOut;

	const handleTitleChange = useStableCallback((newTitle: string) => {
		if (isLivePage) {
			/*
				Live pages immediately persist title changes to the backend and change the page title in the UI,
				unlike regular pages where changing the title only alters the draft version of the page and
				it is not fully persisted until the user saves the draft.
				*/
			updateContentTypesTitle(newTitle);
			return;
		}

		setOptimisticTitle(newTitle);

		triggerSaveIndicator();
		onTitleChange(newTitle);
		triggerSaveDraft();

		// Update timestamp cache so that Last Edited in object header responsively updates
		if (fg('confluence_frontend_content_wrapper')) {
			updateTimestampCache({
				contentId,
			});
		}
	});

	useStartLivePageEditExperience({
		editorApi: fullPageEditorPresetAPI || undefined,
		contentId,
	});

	const { openPublishModal } = usePublishModalMountActions();
	const validatePageAndOpenModal = usePrePublishTitleValidation({
		contentId,
		contentType,
		isUnpublishedDraft,
		flags,
		retrieveEditorTitleAndContent,
		onBeforeValidation: useCallback(() => {
			void saveDraft?.();
		}, [saveDraft]),
		onValidationSuccess: openPublishModal,
		analyticsSource: 'LivePagesDetailsPanel',
		optimisticTitle,
	});

	const handleBreadcrumbsClick = useCallback(
		(destinationUrl: string) => {
			// https://product-fabric.atlassian.net/browse/CEMS-2479
			// sync draft and content title with NCS
			// when user click on breadcrumbs to exit the editor
			// so title is up-to-date in page tree
			return new Promise<void>((resolve) => {
				void saveDraft({
					isOnClose: true,
				}).then(() => {
					push(destinationUrl);
					resolve();
				});
			});
		},
		[push, saveDraft],
	);

	// [ED-27497] IMPORTANT: This has been moved to the below function. If you add anything here, also add it to the new function.
	// `quickInsertPluginOptions` function in 'packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/quickInsertPluginOptions.ts'.
	const elementBrowser = useMemo(
		() => ({
			showModal: true,
			replacePlusMenu: true,
			emptyStateHandler: getMarketplaceAppsContainer,
		}),
		[],
	);

	// IMPORTANT: If you add a new feature flag here, please make sure to add it to the
	// `featureFlagsPluginOptions` function in 'packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/featureFlagsPluginOptions.ts`.
	const fullPageEditorFeatureFlags = useMemo(
		() => ({
			...editorFeatureFlags,
			...editorExperiments,
			...{
				'basic-text-transformations':
					// utilise this flag as part of eligibility check to avoid passing AI enablement status as editor prop
					aiGlobalOptIn.status === 'disabled',
			},
		}),
		[editorFeatureFlags, editorExperiments, aiGlobalOptIn.status],
	);

	const onPreviewClicked = useCallback(
		(requestingPreviewAsBlog: any, stagedMoveParams: any) => {
			setPreviewAsBlog(requestingPreviewAsBlog === true);
			setShouldRenderPreview(true);
			setStagedContentId(stagedMoveParams?.newParentPageId);
			setStagedSpaceKey(stagedMoveParams?.newSpaceKey);
		},
		[setPreviewAsBlog, setShouldRenderPreview, setStagedContentId, setStagedSpaceKey],
	);

	const onHoverPublish = useCallback(() => {
		if (
			preloadNativeCollabRefetch &&
			expValEquals('confluence_publish_ivc_native_collab_query', 'isEnabled', true)
		) {
			void preloadNativeCollabRefetch();
		}
	}, [preloadNativeCollabRefetch]);

	const onClickPublish = useStableCallback(onPublish);
	const navigateOnCloseStable = useStableCallback(navigateOnClose);

	// Insert a snippet's contents if there is a snippet id in the url
	useInsertSnippetOnLoad(fullPageEditorPresetAPI, collabEditInitialized);

	const { isExternalCollaborator } = useIsExternalCollaborator();

	const { getHeadingLinkPath } = useHeadingLinksPathBuilder({
		contentId,
		spaceKey,
		contentType,
		title,
		createdDate,
		isUnpublishedDraft,
	});

	const editorDisabled = useEditorContentDisabled({
		disabled,
		collabEditInternetLost,
		internetDisconnected,
		api: fullPageEditorPresetAPI,
	});

	const extensionRendererViewOptions = useExtensionRendererViewOptions({
		getExtensionHandlers: rendererExtensionOptions?.getExtensionHandlers,
		isLivePage,
		contentId,
		spaceKey,
	});

	const getExtensionHeight = useExtensionNodeParams();

	const { isDesktop } = useIsDesktop();

	const finalExtensionProviders = extensionProvidersWithLCM;

	const ssrExtensionHandlers = useEditorSSRExtensionHandlers(
		contentId,
		spaceKey,
		finalExtensionProviders,
	);

	const FullPageEditorComponentWrapper = isCompanyHub ? HubEditorWrapper : FullPageEditorWrapper;

	const { canUndo, canRedo, undo, redo, activateFindReplace } =
		useEditorActions(fullPageEditorPresetAPI);

	const editorActionsProps =
		fg('aifc_create_enabled') || editorExperiment('platform_editor_controls', 'variant1')
			? {
					canUndo,
					canRedo,
					undo,
					redo,
					activateFindReplace,
				}
			: undefined;

	const allowInlineComments = isEmbeddedConfluence ? hasInlineComments : true;

	const shouldHideCommentsInEmbeddedLivePage =
		isLivePage &&
		(UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) ||
			fg('platform_native_embeds_rollout_non_maui_experience'));

	const allowPageComments =
		!isEmbeddedConfluence || (hasPageComments && !shouldHideCommentsInEmbeddedLivePage);

	const {
		isTemplateBrowserEnabled,
		isTemplateBrowserPanelEnabled,
		isBlankPageQuickActionsEnabled,
	} = useEmbeddedEditPageFeatures();
	const { hasModernHeader, hideHelpButton } = useEmbeddedViewPageFeatures();
	const showEmbeddedModernHeader = isEmbeddedConfluence ? hasModernHeader : !isEmbeddedConfluence;

	const shouldHideHelpWidget =
		isEmbeddedConfluence &&
		(hideHelpButton ||
			(isLivePage &&
				(UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) ||
					fg('platform_native_embeds_rollout_non_maui_experience'))));
	const showHelpWidget = !shouldHideHelpWidget;

	const shouldHideLiveDocEmbeddedEditorToolbar =
		isLivePage &&
		isEmbeddedConfluence &&
		(UNSAFE_expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) ||
			fg('platform_native_embeds_rollout_non_maui_experience'));

	const setSsrRenderMark = useCallback(() => {
		recordSSRMeasure({
			segmentName: SSR_TRACE_SEGMENT_NAME,
			startTimestamp: firstRenderStartTimestampRef.current,
			endTimestamp: performance.now(),
		});
	}, []);

	return (
		<UFOLabel name="full-page-editor-component">
			<SaveIndicatorContextProvider editorApi={fullPageEditorPresetAPI}>
				<EditorContextPanelContextProvider editorAPI={fullPageEditorPresetAPI}>
					<ClassificationModalContextProvider>
						<EditorContextContentCreationPanelWrapper
							cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
							collabEditInitialized={collabEditInitialized}
							contentId={contentId}
							contentType={contentType}
							draftShareId={draftShareId}
							editorActions={editorActions}
							hideSpinner={hideSpinner}
							isUnpublishedDraft={isUnpublishedDraft}
							renderPanel={!shouldRenderPreview}
							showSpinner={showSpinner}
							title={title}
						>
							{({
								isContentCreationPanelOpen,
								contentCreationPanelMode,
								onClickOpenContentCreationPanel,
								beforeUpdateRequest,
								postUpdateRequest,
							}) => (
								<ContentAppearanceContextProvider contentId={contentId}>
									<ContentAppearanceContextConsumer>
										{({ contentAppearance }) => {
											let appearance: FullPageAppearances = getContentAppearance(
												contentAppearance,
												draftAppearance,
											);

											if (isCompanyHub) {
												// Company Hub editors no longer have the option of changing page width,
												// as content topper is removed. For users who had previously set Company
												// Hub page to 'full-width' and can no longer change it back, we force it to
												// 'full-page' to fix unexpected layout issues and allow editors to
												// set the width on individual extension instances.
												appearance = 'full-page';
											}
											const referentialityContext = getReferentialityContext(
												createAnalyticsEvent,
												contentId,
												appearance,
												ssrExtensionHandlers,
												extensionRendererViewOptions,
												getExtensionHeight,
											);

											// IMPORTANT: If you change the code below, please also update the
											// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/placeholderPluginOptions/placeholderPluginOptions.ts
											const placeholderText =
												!viewMode &&
												_getPlaceholderText({
													featureFlags,
													isAIEnabledForContent,
												});

											const metaKey = isMac() ? '⌘' : 'Ctrl';

											return (
												<FullPageEditorComponentWrapper>
													<BlankPageQuickActionsProvider spaceKey={spaceKey}>
														<HeadingLinksStyleWrapper>
															<EditorWrapper {...wrapperProps}>
																{isLivePage && (
																	<>
																		<PublishModalMountPoint
																			onClickPublish={onClickPublish}
																			onClickPreview={onPreviewClicked}
																			retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
																			publishButtonDisabled={false}
																			title={title ?? draftTitle}
																			userViewingPreview={false}
																			isUnpublishedDraft={false}
																			contentType={contentType}
																			shouldShowWorldIcon={false}
																			shouldShowFooterButtons={false}
																			isConvertToPageDialog
																			onHoverPublish={onHoverPublish}
																		/>
																		<PublishModalKeyboardShortcuts
																			contentId={contentId}
																			contentType={contentType}
																			isUnpublishedDraft={isUnpublishedDraft}
																			hasEditPermission={hasEditPermission}
																			flags={flags}
																			retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
																			saveDraft={saveDraft}
																		/>
																	</>
																)}
																{editorExperiment('platform_editor_controls', 'variant1') ? (
																	<FullPageEditorBannerWrapper
																		isLivePage={isLivePage}
																		isEmbeddedConfluence={isEmbeddedConfluence}
																		viewMode={viewMode}
																		spaceKey={spaceKey}
																		editorFullPagePresetAPI={fullPageEditorPresetAPI}
																		triggerAnalyticsEvent={triggerAnalyticsEvent}
																		contentId={contentId}
																		version={version}
																		isShowingOfflineBanner={collabEditInternetLost}
																	/>
																) : (
																	<>
																		{!isLivePage && fg('aifc_create_enabled') && (
																			<SmartCreateFeedbackBannerLoader contentType="page" />
																		)}
																	</>
																)}
																{!isLivePage && fg('analytics_for_large_candidate_pages') && (
																	<LazyLargePageSplitCandidateDetector
																		contentId={contentId}
																		draftBody={draftBody ?? null}
																		proseMirrorRenderedData={proseMirrorRenderedData}
																	/>
																)}
																{showEmbeddedModernHeader &&
																	!shouldHideLiveDocEmbeddedEditorToolbar &&
																	(fg('confluence_frontend_content_wrapper') ? (
																		<EditorObjectHeaderContextProvider
																			value={{
																				saveDraft,
																				navigateOnClose,
																				triggerAnalyticsEvent,
																				version,
																				editorActions,
																				title,
																				onClickOpenContentCreationPanel,
																				isContentCreationPanelOpen,
																				contentCreationPanelMode,
																				retrieveEditorTitleAndContent,
																				showRevertDialog,
																				onPreviewClicked,
																				hasEditPermission,
																				isPublishingContent,
																				userOffline,
																				forceDisabled,
																				editorTitle: title ?? draftTitle,
																				collabLimitReached,
																				collabEditConnected,
																				onClickPublish,
																				shouldRenderPreview,
																				isUnpublishedDraft,
																				validatePageAndOpenModal,
																				...editorActionsProps,
																				viewMode,
																				onHoverPublish,
																			}}
																		>
																			<ExperienceStart name={OBJECT_HEADER_EXPERIENCE} />
																			<ContentTypesHeader contentId={contentId} />
																			{isLivePage && fg('aifc_create_enabled') && (
																				<SmartCreateFeedbackBannerLoader contentType="page" />
																			)}
																		</EditorObjectHeaderContextProvider>
																	) : (
																		<EditorActionBar
																			spaceKey={spaceKey}
																			editorActions={editorActions}
																			collabEditState={collabEditState}
																			title={title ?? draftTitle}
																			forceDisabled={forceDisabled}
																			userOffline={userOffline}
																			contentId={contentId}
																			contentType={contentType}
																			isContentCreationPanelOpen={isContentCreationPanelOpen}
																			isUnpublishedDraft={isUnpublishedDraft}
																			onClickPublish={onClickPublish}
																			onClickOpenContentCreationPanel={
																				onClickOpenContentCreationPanel
																			}
																			onPreviewClicked={onPreviewClicked}
																			saveDraft={saveDraft}
																			collabLimitReached={collabLimitReached}
																			showRevertDialog={showRevertDialog}
																			retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
																			isPublishingContent={isPublishingContent}
																			isSingleUserSession={isSingleUserSession}
																			navigateOnClose={navigateOnCloseStable}
																			triggerAnalyticsEvent={triggerAnalyticsEvent}
																			shouldRenderPreview={shouldRenderPreview}
																			shouldShowRecentSpotlight={shouldShowRecentSpotlight}
																			checkIfConditionPreventingPublish={
																				checkIfConditionPreventingPublish
																			}
																			flags={flags}
																			createAnalyticsEvent={createAnalyticsEvent}
																			hasEditPermission={hasEditPermission}
																			editorDisabled={disabled}
																			isTitleFocused={isTitleFocused}
																			viewMode={viewMode}
																			contentClassificationMetadata={contentClassificationMetadata}
																			isLivePage={isLivePage}
																			requestToEditContent={requestToEditContent}
																			hasRequestedEditPermission={hasRequestedEditPermission}
																			ellipsisEditorActions={editorActionsProps}
																		/>
																	))}
																{!shouldRenderPreview && <ApprovalInProgressEditorBlocker />}
																<EditorDisplayWrapper
																	data-testid="full-page-editor-style"
																	//TODO: Control isHidden prop
																	isHidden={shouldRenderPreview}
																	isEditorActionBarRendered
																	isToolbarAifcEnabled={fg('aifc_create_enabled')}
																>
																	<EditorConnectionBannerWrapper
																		editorType="next"
																		triggerAnalyticsEvent={triggerAnalyticsEvent}
																		collabEditState={collabEditState}
																		collabEditInternetLost={collabEditInternetLost}
																		collabEditConnectionTimedOut={collabEditConnectionTimedOut}
																		willEditorDisable={!editorDisabled}
																		contentType={contentType}
																		contentId={contentId}
																		spaceId={spaceId}
																		fullPageEditorPresetAPI={fullPageEditorPresetAPI}
																		isViewMode={viewMode || false}
																		isLivePage={isLivePage}
																		isDesktop={isDesktop}
																	/>
																	{showHelpWidget && (
																		<OnboardingHelpContainer
																			contentId={contentId}
																			isContentCreationPanelOpen={isContentCreationPanelOpen}
																			onClickOpenContentCreationPanel={
																				onClickOpenContentCreationPanel
																			}
																			editorActions={editorActions}
																			isShowingOfflineBanner={collabEditInternetLost}
																			version={version}
																			spaceKey={spaceKey}
																			editorApi={fullPageEditorPresetAPI}
																			handleTitleChange={handleTitleChange}
																		/>
																	)}
																	<PageLoadMark name="onBeforeEditor" />
																	<MacroExperienceStart
																		contentId={contentId}
																		mode={EDITOR}
																		name={VIEW_ALL_MACRO_EXPERIENCE}
																		timeout={ExperienceTimeout.MACRO_LOAD}
																	/>
																	<PageCommentsPubSubHandler
																		contentId={contentId}
																		contentType={contentType}
																	/>
																	<EditorAnalyticsListener
																		contentId={contentId}
																		contentType={contentType}
																		source="pageAndBlogEditor" // TODO: add isTemplateEditorCheck
																		collabService={CollabServiceProperty.NCS}
																		onProseMirrorRendered={onProseMirrorRendered}
																		isGuest={isExternalCollaborator}
																	>
																		{
																			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
																			!process.env.REACT_SSR &&
																				proseMirrorRenderedData &&
																				expVal(
																					'cc_comments_new_comments_data_manager',
																					'isEnabled',
																					false,
																				) && (
																					<CommentsDataBackgroundLoader
																						key={`CommentsDataBackgroundLoader-${contentId}`}
																						contentId={contentId}
																					/>
																				)
																		}
																		<PrimaryToolbarContextProvider
																			value={{
																				forceDisabled,
																				userOffline,
																				collabLimitReached,
																				contentId,
																				contentType,
																				isContentCreationPanelOpen,
																				isUnpublishedDraft,
																				onClickPublish,
																				onClickOpenContentCreationPanel,
																				onPreviewClicked,
																				saveDraft,
																				spaceKey,
																				showRevertDialog,
																				retrieveEditorTitleAndContent,
																				isPublishingContent,
																				isSingleUserSession,
																				triggerAnalyticsEvent,
																				navigateOnClose: navigateOnCloseStable,
																				title,
																				editorActions,
																				disabled: disabled || collabLimitReached,
																				isTitleFocused,
																				collabEditState,
																				shouldRenderPreview,
																				shouldShowRecentSpotlight,
																				flags,
																				createAnalyticsEvent,
																				hasEditPermission,
																				ellipsisEditorActions: editorActionsProps,
																			}}
																		>
																			<ContentComponentsContextProvider
																				value={{
																					contentComponentsTitle:
																						title ?? draftTitle ?? cachedTitle,
																					contentComponentsDisabled:
																						disabled ||
																						collabLimitReached ||
																						!collabEditInitialized ||
																						isDraftLockedForEditing,
																					isDraftLockedForEditing,
																					handleTitleChange,
																					breadcrumbsClickHandler: handleBreadcrumbsClick,
																					beforeUpdateRequest,
																					postUpdateRequest,
																				}}
																			>
																				<MultiMacroQueryContainer
																					contentId={contentId}
																					featureFlags={featureFlags}
																					readFragmentInterop={readFragmentInterop}
																					contentRendererMode={EDITOR}
																				>
																					<MentionsNotificationsWrapper
																						contentId={contentId}
																						flags={flags}
																						isLivePage={isLivePage}
																						isMentionsNotificationsEnabled={
																							isLivePage || isClassicMentionsNotificationsEnrolled
																						}
																						editorActions={editorActions}
																					>
																						<InlineCommentsProvider>
																							<ObjectCommentProvider>
																								<GuestMentionsNotificationsWrapper
																									dialogs={dialogs}
																									flags={flags}
																								>
																									<EditorLinkingFeatures
																										options={CARD_OPTIONS}
																										viewMode={viewMode}
																										isLivePage={isLivePage}
																									>
																										{(editorLinkingProps) => {
																											// TODO: fix me. ignored to get an unrelated refactor in: React Hooks must be called in a React function component or a custom React Hook function
																											/* eslint-disable react-hooks/rules-of-hooks */
																											const mainButtonsContext =
																												usePrimaryToolbarContext();
																											/* eslint-enable react-hooks/rules-of-hooks */

																											const primaryToolBarComponent =
																												!showEmbeddedModernHeader &&
																												!shouldHideLiveDocEmbeddedEditorToolbar ? (
																													<EditorPrimaryToolbarComponents
																														{...mainButtonsContext}
																														allowInlineComments={
																															allowInlineComments
																														}
																														allowPageComments={allowPageComments}
																													/>
																												) : undefined;

																											return (
																												<>
																													{
																														<QuickReload
																															contentId={contentId}
																															contentType={contentType}
																															// The 'Date.now()' is expedient; see notes in quick-reload
																															// package on what a more correct lastPollTime value would be.
																															lastPollTime={Date.now()}
																															pageMode={
																																isLivePage
																																	? PageMode.LIVE
																																	: PageMode.EDITOR
																															}
																															showCommentsPanel={showCommentsPanel}
																														/>
																													}
																													<ErrorBoundary
																														markErrorCriticalInSSR
																														attribution={Attribution.EDITOR_CORE}
																													>
																														<FullPageComposableEditor
																															onSSRMeasure={
																																recordSSRMeasureWithPrefix
																															}
																															setEditorAPI={setEditorApi as any}
																															onShowSuggestionsSidebar={
																																onShowSuggestionsSidebar
																															}
																															onOpenRovoChatWithSkill={
																																onOpenRovoChatWithSkill
																															}
																															// Needed to create the referentiality plugin in the
																															// ComposableEditor
																															referentialityContext={
																																referentialityContext
																															}
																															editorPluginAIOptions={
																																editorPluginAIOptions
																															}
																															aiGlobalOptIn={aiGlobalOptIn}
																															editorPluginAIFeedbackHandlers={
																																editorPluginAIFeedbackHandlers
																															}
																															// === Config ===
																															{...extensionOptions}
																															extensionProviders={
																																extensionProvidersWithLCM
																															}
																															getExtensionHeight={
																																getExtensionHeight
																															}
																															allowConfluenceInlineComment={
																																allowInlineComments
																															}
																															allowFindReplace={
																																!collabEditInternetLost &&
																																findReplaceOptions
																															}
																															defaultValue={draftBody ?? undefined}
																															elementBrowser={elementBrowser}
																															media={mediaOptions}
																															mention={mentionProps}
																															placeholder={
																																placeholderText &&
																																intl.formatMessage(
																																	placeholderText,
																																	{ key: metaKey },
																																)
																															}
																															waitForMediaUpload
																															__rendererExtensionOptions={
																																extensionRendererViewOptions
																															}
																															// === State config ===
																															appearance={appearance ?? 'full-page'}
																															contentMode={
																																expValEquals(
																																	'cc_editor_ai_content_mode',
																																	'forceAll',
																																	true,
																																)
																																	? 'compact'
																																	: editorContentMode
																															}
																															disabled={
																																isTitleFocused ||
																																editorDisabled ||
																																collabLimitReached
																															}
																															onChange={handleDocChange}
																															shouldFocus={
																																!!title && shouldFocusEditor
																															}
																															onEditorReady={onEditorReady}
																															// === Providers ===
																															activityProvider={activityProvider}
																															// In future, inlineCommentOptions should check if isTemplateEditor
																															annotationProviders={
																																allowInlineComments
																																	? annotationProviderOptions
																																	: undefined
																															}
																															autoformattingProvider={
																																autoformattingProvider
																															}
																															collabEditProvider={
																																collabEditProvider
																															}
																															collabEdit={collabEdit}
																															contextIdentifierProvider={
																																contextIdentifierProvider
																															}
																															emojiProvider={emojiProvider}
																															mentionProvider={mentionProvider}
																															searchProvider={searchProvider}
																															taskDecisionProvider={
																																taskDecisionProvider
																															}
																															syncedBlockProvider={
																																syncedBlockProvider
																															}
																															// === Components ===
																															contentComponents={_getContentComponents(
																																{
																																	contentId,
																																	debounceTitleChangeUpdates,
																																	focusEditor,
																																	onFocusChanged,
																																	userOffline,
																																	linkCreation:
																																		linkCreation === 'true',
																																	editorPluginAIFeedbackHandlers,
																																	isAdminHubAIEnabled,
																																	isAIEnabledForContent,
																																	isAIEarlyAccessEnabled,
																																	hideSpinner,
																																	templateSuggestionsRef,
																																	templateButtonRef,
																																	editorActions,
																																	isTitleFocused,
																																	disabled,
																																	viewMode,
																																	contentClassificationMetadata,
																																	appearance,
																																	spaceKey,
																																	title,
																																	cancelDebouncedSaveDraft,
																																	showSpinner,
																																	onClickOpenContentCreationPanel,
																																	isUnpublishedDraft,
																																	featureFlags,
																																	fullPageEditorPresetAPI,
																																	showBlankPageQuickActions,
																																	isLivePage,
																																	version,
																																	isEditorEmpty,
																																	shouldRenderPreview,
																																	isEditorAnnotationProviderManagersEnabled:
																																		fg(
																																			'confluence_frontend_editor_annotation_manager',
																																		),
																																	editorApi:
																																		fullPageEditorPresetAPI,
																																	contentType,
																																	onTitleChange,
																																	loadingPriority,
																																	isRovoButtonEnabled,
																																	editorSessionId,
																																	editorActionsProps,
																																	isSeamlessTransitionInProgress:
																																		fg(
																																			'confluence_frontend_transition_preloading_system',
																																		) &&
																																		isLiveDocSeamlessTransitioning,
																																	hasByLineContributors,
																																	hasByLineExtensions,
																																	hasReactions,
																																	hasPageComments,
																																	isEmbeddedConfluence,
																																	isTemplateBrowserEnabled,
																																	isTemplateBrowserPanelEnabled,
																																	isBlankPageQuickActionsEnabled,
																																	isRovoLLMEnabled,
																																	...(isRovoLLMEnabled &&
																																		isExperimentEnabled(
																																			'cwr_blank_object_experiment',
																																		) && {
																																			customPlaceholderOverride,
																																			onCreateWithRovoClick:
																																				handleCreateWithRovo,
																																			shouldShowCreateWithRovoExperience,
																																			onPromptMouseEnter,
																																			onPromptMouseLeave,
																																			onPromptClick,
																																			onPromptSubmit,
																																			onDismissCwr,
																																			hasSubmittedCwrPrompt,
																																			onInputEmpty,
																																			orgId,
																																			isInlineAIOpen,
																																		}),
																																},
																															)}
																															contextPanel={_getContextPanelComponent()}
																															featureFlags={
																																fullPageEditorFeatureFlags
																															}
																															performanceTracking={
																																performanceTrackingOptions
																															}
																															{...editorLinkingProps}
																															viewMode={
																																viewMode || isDraftLockedForEditing
																																	? 'view'
																																	: 'edit'
																															}
																															__isRemixHidden={!isRovoLLMEnabled}
																															__isRovoLLMEnabled={isRovoLLMEnabled}
																															__remixDisabledReason={
																																remixDisabledReason
																															}
																															__livePage={isLivePage}
																															isEmbeddedConfluence={
																																isEmbeddedConfluence
																															}
																															flags={flags}
																															primaryToolbarComponents={
																																primaryToolBarComponent
																															}
																															hasEditPermission={hasEditPermission}
																															requestToEditContent={
																																requestToEditContent
																															}
																															hasRequestedEditPermission={
																																hasRequestedEditPermission
																															}
																															getHeadingLinkPath={
																																getHeadingLinkPath
																															}
																															contentId={contentId}
																															spaceId={spaceId}
																															spaceKey={spaceKey}
																															contentType={contentType}
																															snippetExtensionList={
																																snippetExtensionList
																															}
																															isAIEnabledForContent={
																																isAIEnabledForContent
																															}
																															showEmbeddedModernHeader={
																																showEmbeddedModernHeader
																															}
																															isCompanyHub={isCompanyHub}
																															syncedBlockEventHandlers={
																																syncedBlockEventHandlers
																															}
																														/>
																													</ErrorBoundary>

																													<EditorCommentShortcutListener
																														contentId={contentId}
																														viewMode={viewMode}
																														editorApi={fullPageEditorPresetAPI}
																													/>
																													{proseMirrorRenderedData !== null && (
																														<TaskScrollComponent />
																													)}
																												</>
																											);
																										}}
																									</EditorLinkingFeatures>
																								</GuestMentionsNotificationsWrapper>
																							</ObjectCommentProvider>
																						</InlineCommentsProvider>
																					</MentionsNotificationsWrapper>
																				</MultiMacroQueryContainer>
																			</ContentComponentsContextProvider>
																		</PrimaryToolbarContextProvider>
																	</EditorAnalyticsListener>
																	<Spinner
																		shouldShowSpinner={
																			shouldShowSpinner ||
																			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
																			(fg('confluence_frontend_transition_preloading_system') &&
																				isLiveDocSeamlessTransitioning)
																		}
																		isOverlayTransparent={
																			!shouldShowSpinner &&
																			fg('confluence_frontend_transition_preloading_system') &&
																			isLiveDocSeamlessTransitioning
																		}
																	/>
																</EditorDisplayWrapper>
																<AIOptInModal />

																<EditorRevertDialog
																	contentId={contentId}
																	spaceKey={spaceKey}
																	isUnpublishedDraft={isUnpublishedDraft}
																	cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
																/>
																<OnboardingEditorModalPrompt
																	onTitleChange={onTitleChange}
																	handlePublish={() =>
																		onPublish({
																			suppressNotifyWatchers: false,
																		})()
																	}
																/>
																<ConditionalPublish
																	contentId={contentId}
																	spaceKey={spaceKey}
																	setCheckIfConditionPreventingPublish={assignConditionalFunction}
																/>
																{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
																{!process.env.REACT_SSR && (
																	<PageLoadWrapper
																		disabled={disabled}
																		contentId={contentId}
																		isCreate={isUnpublishedDraft}
																		proseMirrorRenderedData={proseMirrorRenderedData}
																		editSource={editSource}
																		extractedCollabEditProvider={extractedCollabEditProvider}
																		isLivePage={isLivePage}
																		customPerfData={customPerfData}
																		draftBody={draftBody}
																	/>
																)}
																{shouldRenderPreview ? (
																	<RendererExtensionContext.Provider
																		value={rendererExtensionOptions}
																	>
																		<PreviewPageComponent
																			contentId={contentId}
																			spaceKey={spaceKey}
																			stagedContentId={stagedContentId}
																			stagedSpaceKey={stagedSpaceKey}
																			contentType={contentType as ContentTypeType}
																			contentAppearance={contentAppearance}
																			title={title}
																			editorActions={editorActions}
																			showTemplateVariablesInPreview
																			changeEditorMode={() => setShouldRenderPreview(false)}
																			Renderer={PageContentRenderer}
																			isPageContentRenderer
																			editorType={EditorTypes.NCS}
																			previewAsBlog={previewAsBlog}
																			contentClassificationMetadata={contentClassificationMetadata}
																		/>
																	</RendererExtensionContext.Provider>
																) : null}
																{contentType === 'page' && <ConvertToLivePageFlag />}
																{expVal(
																	'confluence_modernized_move_dialog',
																	'isEnabled',
																	false,
																) && <MovePageSuccessFlag />}

																{!isEmbeddedConfluence && (
																	<RovoBridgeCommandListener
																		contentId={contentId}
																		contentLocation="global"
																		editorAPI={fullPageEditorPresetAPI}
																		setEditorDisabled={setForceDisabled}
																		mediaProvider={mediaOptions?.provider}
																		onTitleChange={
																			hasSubmittedCwrPrompt ? handleTitleChange : undefined
																		}
																		onStreamEnd={onCwrStreamEnd}
																		isPreviewMode={shouldRenderPreview}
																	/>
																)}
															</EditorWrapper>
														</HeadingLinksStyleWrapper>
														{_getEditorContextConnectionsPanel({
															referentialityContext,
															connectionsPanelActiveNodeLocalId,
															setConnectionsPanelActiveNodeLocalId,
															closeConnectionsPanel,
														})}
														{
															// This FF controls if the right rail config panel is available in when editing a page
															isSmartButtonAllowed
																? _getEditorContextSmartButtonConfigPanel({
																		smartButtonActiveNodeLocalId,
																		closeSmartButtonConfigPanel,
																		fullPageEditorPresetAPI,
																	})
																: null
														}
														<EditorContextCustomSitesConfigPanelEntryPoint
															{...{
																localId: activeNodeLocalId || '',
																closeSelf: closeConfigPanel,
																editorApi: fullPageEditorPresetAPI || undefined,
																contentId,
																upsellModalSource,
																setUpsellModalSource,
															}}
														/>
													</BlankPageQuickActionsProvider>
												</FullPageEditorComponentWrapper>
											);
										}}
									</ContentAppearanceContextConsumer>
								</ContentAppearanceContextProvider>
							)}
						</EditorContextContentCreationPanelWrapper>
					</ClassificationModalContextProvider>
				</EditorContextPanelContextProvider>
			</SaveIndicatorContextProvider>
			{/** Any hooks that are not necessary for directly re-rendering the editor should move here */}
			<ConditionalSideEffectsComponent
				contentId={contentId}
				contentType={contentType}
				isLivePage={isLivePage}
				isUnpublishedDraft={isUnpublishedDraft}
				title={title}
				draftTitle={draftTitle}
				isAIEnabledForContent={isAIEnabledForContent}
				handleTitleChange={handleTitleChange}
				collabEditInternetLost={collabEditInternetLost}
				internetDisconnected={internetDisconnected}
				extractedCollabEditProvider={extractedCollabEditProvider}
				fullPageEditorPresetAPI={fullPageEditorPresetAPI}
				isDocumentActive={isDocumentActive}
				focusedCommentId={focusedCommentId}
				replyToCommentId={replyToCommentId}
				handleUseFocusedCommentGQLError={handleUseFocusedCommentGQLError}
				annotationStateRef={annotationStateRef}
				addCommentInCache={addCommentInCache}
				deleteCommentInCache={deleteCommentInCache}
				updateUnresolvedInlineCommentFn={updateUnresolvedInlineCommentFn}
				triggerAnalyticsEvent={triggerAnalyticsEvent}
				proseMirrorRenderedData={proseMirrorRenderedData}
				showBlankPageQuickActions={showBlankPageQuickActions}
				hasSubmittedCwrPrompt={hasSubmittedCwrPrompt}
				shouldShowCreateWithRovoExperience={shouldShowCreateWithRovoExperience}
				isRovoLLMEnabled={isRovoLLMEnabled}
				onSpaceKeyPressed={handleCreateWithRovo}
				flags={flags}
				intl={intl}
				disabled={disabled}
				isTitleFocused={isTitleFocused}
				shouldRenderPreview={shouldRenderPreview}
				collabEditError={collabEditError}
				collabLimitReached={collabLimitReached}
				heartbeatError={heartbeatError}
				removeCommentQueryParams={removeCommentQueryParams}
				setEditorAnnotations={setEditorAnnotations}
				getSortedMarkerRefs={getSortedMarkerRefs}
				setOrderedActiveAnnotationIdList={setOrderedActiveAnnotationIdList}
				editorAnnotationsRef={editorAnnotationsRef}
			/>
			<DocumentEditPageTitle
				contentType={contentType as ContentTypeType}
				isCreatingNew={isUnpublishedDraft}
				spaceKey={spaceKey}
				title={title}
				isLivePage={isLivePage}
			/>
			<EditorNavigationLockout
				contentId={contentId}
				isPublishingContent={isPublishingContent}
				isFirstParticipant={isFirstParticipant}
				editorPresetAPI={fullPageEditorPresetAPI}
			/>
			{printLockout}

			{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
			{process.env.REACT_SSR && <RenderMarker onRender={setSsrRenderMark} />}
			{shouldShowSpotlight &&
				newLinkTypes.length > 0 &&
				fg('link-paste-spotlight-experiment-gate') && (
					<IntegrationLinkSpotlightLoadable
						detectedLinks={detectedLinks}
						newLinkTypes={newLinkTypes}
						newLinks={newLinks}
						onClose={closeSpotlight}
						cloudId={cloudId}
						atlassianAccountId={userId ?? null}
						analyticsContext="editor"
					/>
				)}
			{expValEquals('confluence_move_to_personal_space_nudge', 'isEnabled', true) && (
				<MoveToPersonalSpaceFlagTrigger
					contentId={contentId}
					contentType={contentType}
					isLivePage={isLivePage}
					spaceKey={spaceKey}
				/>
			)}
		</UFOLabel>
	);
};

export const FullPageEditorComponent = withRovoSystemSpaceNotFound(
	withDocChangeContext(
		withEditorContext(
			withEditorActions(
				withFlags(withDialogs(withPageTreeStateUpdater(FullPageEditorComponentImpl))),
			),
		),
	),
);
